/**
 * `<CombatApp>` — the content of the GM desktop's **COMBAT.app** window: the live combat surface
 * for one encounter. Opened when an encounter is created or picked from ENCOUNTER.app. At its
 * conception it is intentionally minimal — the **encounter title** at the top, and the bare
 * {@link CombatStage} beneath — the seam the rest of combat (tokens, turn order, targeting) grows
 * into.
 *
 * **It is a client of the encounter ws room** (`useEncounter`): the title and grid read live
 * `EncounterState`, so a rename or mode change elsewhere reflects here. Identity rides the session
 * token, exactly as the other room clients do.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  gridExtent,
  worldFor,
  movePathAnchors,
  mayPlayMove,
  controlsCombatant,
  heldAction,
  encounterAnnotations,
  projectForDisplay,
  perUnitState,
  type Combatant,
  type EncounterCommand,
  type SquareCell,
  encounterGround,
  encounterCommitScope,
  isCombatOver,
} from "@lancer-vtt/domain";
import {
  isMoveCheckpointPromptId,
  checkpointMoverId,
  moveCheckpointWatcherId,
  OVERWATCH_TAKE_OPTION,
  OVERWATCH_DECLINE_OPTION,
} from "@lancer-vtt/rules";
import { square, squareKey } from "@lancer-vtt/space";
import { faAddressCard, faDoorOpen, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useCompendiumContext } from "../../../lib/CompendiumProvider";
import { useEncounter } from "../../../lib/encounter/useEncounter";
import { BrowserMatchHandle } from "../../../lib/encounter/browserMatchHandle";
import { useAutoplayBot } from "../../../lib/encounter/autoplayBot";
import type { UseRoster } from "../../../lib/roster/useRemoteRoster";
import { useStage } from "../shell/stageContext";
import { buildTokenImageCtx } from "../../../lib/encounter/tokenImageCtx";
import { tokenChromeFor } from "../../../lib/encounter/tokenChrome";
import { useGameSessionContext } from "../../../lib/game/GameSessionProvider";
import { useSlotNames } from "../../../lib/game/useSlotNames";
import { CombatStage, type CombatTool } from "../CombatStage";
import { RoundHeader } from "./RoundHeader";
import { TurnBanner } from "./TurnBanner";
import { EndCombatPanel } from "./EndCombatPanel";
import { StaleContentNotice } from "../StaleContentNotice";
import { CommsApp } from "../CommsApp";
import { animateMoveBudget, asSquareCell, draggedRoute, footprintBox, CELL } from "../combatCells";
import { weaponProfileForAim } from "../../../lib/encounter/attackRange";
import { usePresentationDirector } from "../../../lib/encounter/usePresentationDirector";
import type { MovePlanner } from "../../../lib/encounter/presentationDirector";
import type { ObjectAuthoringPlan } from "../objectAuthoring";
import { DockZonePreview } from "../DockZonePreview";
import { useCombatWindows, type CombatWindowSpec } from "../../../lib/encounter/windows";
import { EscLayersContext, useEscEntry, useEscLayers } from "../../../lib/encounter/escLayers";
import {
  InspectorBody,
  EditInspectorBody,
  UnitsBody,
  DeployPickerBody,
  MapSetupBody,
  TerrainLegendBody,
  TileInfoBody,
  RoomInspectorBody,
  type PickerKind,
} from "../../../lib/encounter/CombatWindows";
import type { EncounterIntent, GameDecisionMessage, PromptOption, WireCell } from "@lancer-vtt/shared";
import { PromptModal } from "../../../lib/encounter/PromptModal";
import { RejectionToast } from "../../../lib/encounter/RejectionToast";
import { AutoActivationBanner } from "../../../lib/encounter/AutoActivationBanner";
import { ReactionWaitBanner } from "../../../lib/encounter/ReactionWaitBanner";
import { deriveResolutionWait } from "../../../lib/encounter/resolutionWait";
import { deriveMoveHoldDecider } from "../../../lib/encounter/moveHoldDecider";
import { MoveHoldBanner } from "../../../lib/encounter/MoveHoldBanner";
import { overwatchTakeWeapon } from "../../../lib/encounter/combatWindows/chooserClassify";
import { TablePausedBanner } from "../../../lib/encounter/TablePausedBanner";
import {
  DEPLOY_PICKER_WINDOW,
  aimResolvesToFire,
  inspectorSpec,
  inspectorCombatantId,
  roomInspectorSpec,
  roomInspectorRoomId,
  techTargetReason,
  appendDirectedDeployPick,
  directedDeployDraftComplete,
  type ActionDraft,
} from "../../../lib/encounter/combatInspector";
import { firstOpenGeometry, readWindowLane } from "../../../lib/encounter/windowLane";
import {
  buildActivateIntent,
  buildAttackIntent,
  buildBarrageIntent,
  buildDismountIntent,
  buildEjectIntent,
  buildReleaseIntent,
  buildRideDismountIntent,
  buildSearchIntent,
  buildMountIntent,
  buildJockeyIntent,
  buildRideIntent,
} from "../../../lib/encounter/attackIntent";
import { throwSteps } from "../../../lib/dice/diceController";
import { markResolutionThrowSource, registerResolutionThrow } from "../../../lib/dice/resolutionThrows";
import { NO_HOLDS, chainSegments, holdKey, segmentThrowKey } from "../../../lib/encounter/chainSegments";
import { holdOpens } from "../../../lib/encounter/sequenceHolds";
import { SequenceHoldBanner } from "../../../lib/encounter/SequenceHoldBanner";
import "../combat-app.css";
import "../combat-windows.css";
import { CombatWindow } from "./CombatWindow";
import { useCombatDice } from "./CombatDice";
import { BoardToolRail, railKeyAction, type RailContext, type ScenePanel } from "./BoardToolRail";
import { useViewPref, VIEW_PREF_KEYS } from "./viewPrefs";
import { viewRowForKey, type ViewContext } from "./viewRows";
import { MapKeyPanel } from "./MapKeyPanel";
import { ActionAsksDock } from "./ActionAsksDock";
import {
  isBackswingReservationFire,
  isSkirmishPrimaryFire,
  multiAttackContinues,
  overwatchTakeAckMatches,
  promptDeciderName,
  promptSourceName,
  shouldKeepSkirmishAimForRider,
  promptIsRequired,
  opensPromptWindow,
  suppressPromptModalForGmBystander,
} from "./predicates";
import { useAuthoringToolbar } from "./useAuthoringToolbar";
import { disarmOnArm, type BoardArmed } from "./armedSeam";
import { useMeasureTool } from "./useMeasureTool";
import { useInspectTool } from "./useInspectTool";
import { InspectReadout } from "./InspectReadout";
import { inspectCell } from "../inspectCell";
import { terrainMechanicsPieces } from "../terrainMechanics";
import { useCombatKeyboard } from "./useCombatKeyboard";
import { useSitrepZones } from "./useSitrepZones";
import { useTargetingWashes } from "./useTargetingWashes";
import { useHoverIntelUnit } from "./useHoverIntelUnit";
import { UnitColumn, unitPanelFor } from "./UnitColumn";
import { unitSheetIdentity } from "../../../lib/encounter/combatWindows/UnitSheet";
import { attackIntentForAim, directAttackIntent, fullTechPickIntent, quickTechPickIntent } from "./attackIntentForAim";
import { isPickingAim, multiTargetBanner, multiTargetCap, multiTargetIntent, togglePick } from "./multiTarget";
import { OVERRIDE_MENU_STATE, OverrideBanner, forcingEncounter, liftsTargetGate } from "./gmOverride";
import { useTargetMiniForecasts } from "./useTargetMiniForecasts";
import { targetTapStep } from "./targetTap";
import { TargetForecastPanel } from "../../../lib/encounter/TargetForecastPanel";
import { previewIfControlled } from "../../../lib/encounter/activationPreview";
import { RangePreviewContext, useRangePreviewSource } from "../../../lib/encounter/rangePreview";
import { advanceFullTechQueue, aimAttackerCanAct, commandMenuStateFor, commandMenuUnitFor, selectOnTurnStart, staleAim, type AimActSeen, type CommandMenuState } from "../../../lib/encounter/commandMenuActions";
import type { CommandMenuScreenId } from "../../../lib/encounter/commandMenu";
import { TokenCommandMenuHost } from "../../../lib/encounter/TokenCommandMenuHost";
import { boostDropRiders, type MovePreview } from "../../../lib/encounter/boostRing";
import { CommandMenuPresence } from "../../../lib/encounter/CommandMenuPresence";
import { TargetingMode, armedHoverTarget, targetingBanner } from "../../../lib/encounter/TargetingMode";
import {
  PICK_ALONG_PATH_OPTION,
  splitAnswerAt,
  splitPickBanner,
  splitPickFor,
  splitPickSightlines,
  type SplitPick,
} from "../../../lib/encounter/coveringFireSplit";
import {
  cellPickAnswerAt,
  cellPickBanner,
  cellPickFor,
  cellPickGhosts,
  cellPickKeys,
  cellPickNeedsConfirm,
  optionHighlight,
  type OptionHighlight,
  type StagedCell,
} from "../../../lib/encounter/cellPick";
import { togglePicked, unitPickAnswer, unitPickBanner, unitPickDimmed, unitPickFor } from "../../../lib/encounter/unitPick";
import type { TargetRingSpec } from "../combatStage/props";

export function CombatApp({
  encounterId,
  token,
  roster,
}: {
  encounterId: string;
  /** The session reclaim token — the encounter socket binds to the verified slot with it. */
  token: string;
  /** The shell's roster room (one socket per client): the deploy picker's mech source and the
   *  END COMBAT commit's target. */
  roster: UseRoster;
}) {
  const { compendium, overlay, loadingOverlay, overlayError } = useCompendiumContext();
  const session = useGameSessionContext();
  const liveEncounter = useEncounter(compendium, encounterId, { token, isGm: session.role === "gm" });
  // slot id → display name, the shared resolver — used below to
  // pre-resolve each remote cursor's label before it reaches the board, which stays dumb and never
  // resolves a name itself (`by` still carries the slot id for identity/colour/keying). Named
  // `slotNameOf`, distinct from the existing combatant-id `nameOf` below (different id space).
  const slotNameOf = useSlotNames();
  const cursorsWithLabels = useMemo(
    () => liveEncounter.cursors.map((c) => ({ ...c, label: slotNameOf(c.by) })),
    [liveEncounter.cursors, slotNameOf],
  );
  // The browser autoplay bot — ONE
  // `BrowserMatchHandle` over the SAME long-lived encounter connection the rest of the UI already
  // rides (never a second connection), and ONE `useAutoplayBot` instance shared by every
  // GM-controlled panel's "play this activation"/"play this side" buttons (the one-bot-run-at-a-
  // time guard). `matchHandle` is null until the controller exists — both play methods are then
  // no-ops (`useAutoplayBot`'s own guard), so the buttons simply stay inert pre-connection.
  const matchHandle = useMemo(
    () => (liveEncounter.controller ? new BrowserMatchHandle(liveEncounter.controller) : null),
    [liveEncounter.controller],
  );
  const bot = useAutoplayBot(matchHandle, compendium);
  const state = liveEncounter.state;
  // Single-selection, shared between the UNITS roster and the stage's tokens. Lifted here so a
  // panel pick arms placement and a token click highlights the same row.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The unit panel grown into the full sheet (`projects/unit-panel.md` Phase 2). It survives a switch
  // of selection and ends on a deselect, Esc or the collapse control.
  const [sheetOpen, setSheetOpen] = useState(false);
  if (sheetOpen && selectedId === null) setSheetOpen(false);
  // The GM's END COMBAT panel (`EndCombatPanel`): END COMBAT opens it rather than ending outright.
  const [endPanelOpen, setEndPanelOpen] = useState(false);
  const authoring = useAuthoringToolbar();
  const {
    brush,
    setBrush,
    tool,
    setTool,
    coverGrade,
    setCoverGrade,
    wallHeight,
    setWallHeight,
    wallCoverGrade,
    setWallCoverGrade,
    hazard,
    setHazard,
    tile,
    eraseMode,
    setEraseMode,
    autoWall,
    setAutoWall,
    objectPreset,
    setObjectPreset,
    objectSectionCount,
    setObjectSectionCount,
    objectArmor,
    setObjectArmor,
    objectOrientation,
    setObjectOrientation,
    objectHeight,
    setObjectHeight,
    drawTool,
    setDrawTool,
    drawColor,
    setDrawColor,
    penMode,
    pickBrush,
    pickTile,
    pickTool,
    pickDrawTool,
  } = authoring;
  // The open SCENE panel (UNITS or Map setup) beside the tool rail. It shares that spot with a
  // BUILD tool's panel: an open SCENE panel covers it (the tool stays armed), and picking a BUILD
  // tool closes the SCENE panel.
  const [scenePanel, setScenePanel] = useState<ScenePanel | null>(null);
  useEffect(() => setScenePanel(null), [brush, tool]);
  // The local edit/play view-mode: EDIT exposes the board-authoring rail and
  // dispatches `place_token` on the canvas (footprint-only authoring placement); PLAY runs the
  // fight (turn HUD + the unit column: the unit panel and its sheet) and dispatches `move_token` (budget-checked).
  // Local GM view-state only — never synced (COMBAT.app is GM-only; there's no second viewer).
  // Default PLAY: you open an encounter to run it; building the board is the deliberate act.
  const [editPlayMode, setEditPlayMode] = useState<"edit" | "play">("play");
  // PLAY-only lockdown for non-GMs: a player is HARD-CLAMPED to
  // PLAY — EDIT is *unreachable*, not merely disabled. The whole authoring surface (place_token /
  // terrain / walls / deploy / base-edit) is GM territory at the server gate, so the player surface
  // never renders it: the clamp drives `useCombatWindows` (player gets only the PLAY registry), every
  // `viewMode === "edit"` branch below stays dead, and the EDIT/PLAY switch itself is GM-gated.
  const isGm = session.role === "gm";
  const viewMode: "edit" | "play" = isGm ? editPlayMode : "play";
  // **GM OVERRIDE** (`decisions/0209`): a sticky GM-only PLAY switch. While it is on, every unit's
  // command menu opens enabled, the attack path's greyed rows and out-of-range targets lift, and an
  // attack goes out wrapped in `gm_force`, which the server resolves past every rule check.
  const [overrideOn, setOverrideOn] = useState(false);
  const override = isGm && viewMode === "play" && overrideOn;
  // Every action intent this app sends goes out wrapped while the override is on: one place, so a
  // command menu row, a sheet chooser and a board click all force alike. Prompt answers re-send
  // through the controller itself and keep their echo's own shape.
  const encounter = useMemo(() => (override ? forcingEncounter(liveEncounter) : liveEncounter), [override, liveEncounter]);
  // The rail's VIEW toggles (`viewRows.ts`), per viewer, remembered per browser.
  const playGrid = useViewPref(VIEW_PREF_KEYS.grid);
  const mapKey = useViewPref(VIEW_PREF_KEYS.mapKey);
  const terrainMechanics = useViewPref(VIEW_PREF_KEYS.terrainMechanics);
  const viewCtx: ViewContext = { grid: playGrid, mapKey, terrainMechanics };
  // The truthful attacker/source-controller waiting-state: derived from the live cross-actor
  // `game_decision` map — never a client-side guess — so it covers an NPC target exactly like a mech
  // target and always names the real decider.
  const resolutionWait = useMemo(() => {
    if (!state) return null;
    return deriveResolutionWait({
      decisions: session.decisions,
      state,
      myActorId: encounter.actorId,
      myRole: session.role,
      pausedMoverId: encounter.tablePausedMoverId,
    });
  }, [session.decisions, state, encounter.actorId, session.role, encounter.tablePausedMoverId]);
  // Who a held move waits on, and why — the decider line on the mover's `MoveHoldBanner` and the
  // bystanders' `TablePausedBanner` (`overwatch-mid-move` 3.3), off the same `game_decision` map.
  const pendingMoverId = encounter.pendingMove?.combatantId;
  const pausedMoverId = encounter.tablePausedMoverId;
  const moveHoldDecider = useMemo(
    () => (state && pendingMoverId !== undefined ? deriveMoveHoldDecider({ decisions: session.decisions, state, moverId: pendingMoverId, viewer: "mover" }) : null),
    [session.decisions, state, pendingMoverId],
  );
  // The table banner reads "your start square" when this viewer's slot drives the paused mover (the
  // mover's own client lands here once a decline commits and clears its predicted hold).
  const tablePausedDecider = useMemo(
    () =>
      state && pausedMoverId !== null
        ? deriveMoveHoldDecider({ decisions: session.decisions, state, moverId: pausedMoverId, viewer: { slot: encounter.actorId } })
        : null,
    [session.decisions, state, pausedMoverId, encounter.actorId],
  );
  // The route-agnostic, PER-MODE GMS//OS window registry: EDIT and
  // PLAY each own a full open-set + geometry, so flipping mode PARKS the current desktop's windows
  // (remembered, not closed) and RESTORES the other's. Windows open on explicit action only.
  // Every first open lands in the free lane, clear of the unit column (panel and command menu), measured
  // at open time (`windowLane.ts`, `decisions/0191`).
  const appRef = useRef<HTMLDivElement | null>(null);
  // The same element as state, so the windows' workspace (COMBAT's box on the stage) re-measures
  // once it mounts: windows float inside the stage, not the browser viewport.
  const [appEl, setAppEl] = useState<HTMLDivElement | null>(null);
  const attachApp = useCallback((el: HTMLDivElement | null) => {
    appRef.current = el;
    setAppEl(el);
  }, []);
  const placeInLane = useCallback((spec: CombatWindowSpec) => {
    const lane = readWindowLane(appRef.current);
    return lane ? firstOpenGeometry(spec, lane) : spec;
  }, []);
  const windows = useCombatWindows(viewMode, placeInLane, appEl);
  // DICE is the shell's rail page (`CombatDice.tsx`): the PLAY sheets' stat cells open it pre-filled.
  const openDice = useCombatDice();
  // The shell around COMBAT: its COMMS column and the GM's preview. Undefined outside it.
  const stage = useStage();
  // Suppress the floating `PromptModal` window for a GM who is merely a BYSTANDER on an
  // OPTIONS-type decision. The wire `encounter_prompt` reaches the decider slot AND every GM slot —
  // a narrower recipient list starves the GM's own `RemoteEncounterController.prompt_` and makes a
  // GM's COMMS-card click a silent no-op — so `encounter.prompt` populates
  // for every GM, decider or not. The window's RENDER stays gated: a GM who genuinely IS the
  // decider (e.g. an NPC-side Brace, where `whoSlotIds` contains `actorId`) still gets the full floating
  // window exactly like any other decider; only a GM resolving SOMEONE ELSE'S reaction loses the
  // window and falls back to the COMMS card instead. Two carve-outs keep this safe: a
  // `"direction"`-type decision NEVER suppresses (the card has no click-a-cell affordance yet —
  // a deferred follow-on) so the GM keeps its board-click backstop; and an
  // absent `whoSlotIds` (a bare/test room, or an NPC with no controlling slot at all) never
  // suppresses either, so a roleless/test room's window is unaffected.
  const suppressModal = suppressPromptModalForGmBystander(encounter.prompt, session.role, session.actorId);
  // A cell or unit pick docks no card: its banner asks the question (`opensPromptWindow`).
  const promptWindowWanted = opensPromptWindow(encounter.prompt, suppressModal);
  // Whether a pending prompt is a spatial DIRECTION decision —
  // the board's twin of the discrete `PromptModal` options list. Drives the click-a-cell board mode
  // below; the prompt itself only ever arrives on this client when we're the who-routed answerer (or
  // a GM recipient, direction or not — B2 above), so no extra recipient check is needed here — the
  // room already only sent it to us.
  const directionPromptActive = encounter.prompt?.answerKind === "direction";
  // A `"cell"` prompt this viewer decides is answered on the board (`cellPick.ts`): its legal spaces
  // wash, a click answers. A GM bystander's copy stays suppressed and answers from the COMMS card.
  const cellPick = useMemo(
    () => (encounter.prompt && !suppressModal ? cellPickFor(encounter.prompt) : null),
    [encounter.prompt, suppressModal],
  );
  // A `"combatant"` prompt this viewer decides is answered by clicking a unit (`unitPick.ts`).
  const unitPick = useMemo(
    () => (encounter.prompt && !suppressModal ? unitPickFor(encounter.prompt) : null),
    [encounter.prompt, suppressModal],
  );
  // A multi-pick's units so far (`"combatants"`), held with its prompt id so a new prompt starts empty.
  const [pickedUnits, setPickedUnits] = useState<{ promptId: string; ids: string[] } | null>(null);
  const livePicked = unitPick?.multi && pickedUnits?.promptId === unitPick.promptId ? pickedUnits.ids : [];
  // The last section's clicked space, waiting on the banner's CONFIRM.
  const [stagedCell, setStagedCell] = useState<StagedCell | null>(null);
  const liveStaged = stagedCell && stagedCell.promptId === cellPick?.promptId ? stagedCell : null;
  // A hovered COMMS decision-card option's space or unit (the GM bystander's list, `optionHighlight`).
  // Held with its prompt id: a card that resolves under the pointer unmounts without a mouse-leave,
  // so the highlight shows only while this client still holds that prompt.
  const [cardHoverFor, setCardHoverFor] = useState<{ promptId: string; highlight: OptionHighlight } | null>(null);
  const onHoverDecisionOption = useCallback((decision: GameDecisionMessage, option: PromptOption | null) => {
    const highlight = option ? optionHighlight(decision.answerKind, option) : null;
    setCardHoverFor(highlight ? { promptId: decision.promptId, highlight } : null);
  }, []);
  const cardHover = cardHoverFor && encounter.hasPrompt(cardHoverFor.promptId) ? cardHoverFor.highlight : null;
  // The one Esc route (`decisions/0189`): surfaces register in fixed layers, and the root handler
  // (`useCombatKeyboard`) dismisses the highest layer's entry, or clears the selection floor.
  const esc = useEscLayers();
  // Which source the transient DEPLOY picker offers (UNITS' ADD ENEMY → npc, ADD MECH → mech). Set
  // when a section's ADD button opens the picker window; the body reads it to pick its content.
  const [pickerKind, setPickerKind] = useState<PickerKind>("npc");
  // The single board-wide armed Skirmish/Activate aim. At most
  // one action is armed board-wide: the aim carries its own `attackerId`, so arming for one unit
  // replaces any other (singular), and only the menu or sheet whose unit matches shows its aim UI.
  // Held here (not the sheet) so the canvas's token-click handler consumes the next click as the target — the `/scratch/map` pattern, square-native.
  const [aim, setAim] = useState<ActionDraft | null>(null);
  // The docked command menu (`projects/token-command-menu.md` 2.1/2.3 and 2c) has no state of its
  // own: it shows the selected unit (`selectedId`) whenever `commandMenuStateFor` admits it, so it
  // can never show a unit other than the selection (see `shownMenu`).
  // The hovered menu row's would-be aim (2.4): washes and target rings only, never click routing.
  const [previewAim, setPreviewAim] = useState<ActionDraft | null>(null);
  // Every other surface listing a weapon or ability (the sheet's loadout rows, the fixed-target
  // Skirmish chooser) previews through this (`rangePreview.tsx`); the menu host shares its owner rule.
  const rangePreview = useRangePreviewSource(setPreviewAim);
  const menuPreviewOwner = useRef({}).current;
  const setMenuPreviewAim = useCallback((a: ActionDraft | null) => (a ? rangePreview.show(menuPreviewOwner, a) : rangePreview.clear(menuPreviewOwner)), [rangePreview, menuPreviewOwner]);
  // The hovered Boost / Disengage row (Phase 3): the movement wash shows where the move would reach.
  const [previewMove, setPreviewMove] = useState<MovePreview | null>(null);
  // The unit column's targeting slot, where the menu host portals a pick's own panel (Ram, a directed deploy).
  const [targetingSlot, setTargetingSlot] = useState<HTMLDivElement | null>(null);
  // The command menu's active screen: on the fire card, targeting mode's banner drops its CANCEL,
  // since the card's own CANCEL (and Esc) is the exit.
  const [menuScreen, setMenuScreen] = useState<CommandMenuScreenId | null>(null);
  // The token under the pointer (the stage's `onTokenHover`); read by `armedHoverTarget` (2c.10) and,
  // hover intel (`useHoverIntelUnit` below).
  const [hoveredTokenId, setHoveredTokenId] = useState<string | null>(null);
  // The touch two-tap (`target-mini-forecasts` 2.1): the target a first tap expanded, tied to the aim
  // it was tapped under so a new or re-armed aim starts fresh. `lastPointerTypeRef` is the last
  // press's `pointerType`, captured before the board's own (compat) `mousedown` reaches `handleSelect`.
  const [touchExpanded, setTouchExpanded] = useState<{ aim: ActionDraft; targetId: string } | null>(null);
  const lastPointerTypeRef = useRef<string | null>(null);
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      lastPointerTypeRef.current = e.pointerType;
    };
    window.addEventListener("pointerdown", onDown, true);
    return () => window.removeEventListener("pointerdown", onDown, true);
  }, []);
  // Targeting mode's CANCEL and stationary right-click (2.5); Esc reaches the same `setAim(null)`
  // through the layer-3 `armed-action` entry.
  const cancelAim = useCallback(() => setAim(null), []);
  // The unit whose turn this client last saw start: when the turn passes to a unit the menu may
  // show and nothing is selected, it is selected, so the menu shows the active unit unasked.
  const lastActorRef = useRef<string | null>(null);

  // The board cell under this client's cursor — the Map key's hovered tile (`terrain-legibility`
  // Phase 2). Held here rather than in the stage because the window is the host's, and reported at
  // cell-change granularity by `CombatStage`'s `onHoverCell`, so this setState fires once per cell
  // crossed rather than once per pointer-move pixel.
  //
  // **Declared HERE, above the loading early-return, for the hook-order invariant** — like every
  // other hook in this component. It was first written down beside `tileArmed` (which is a
  // plain derivation, not a hook, and correctly lives below), and that shipped a conditional
  // `useState`: on the loading→loaded flip React saw a different hook count and threw the minified
  // #310 that blanked the whole COMBAT route behind its error boundary. Typecheck, lint and the unit
  // suite were all green through it — only the browser tier caught it.
  const [hoverInfoCell, setHoverInfoCell] = useState<SquareCell | null>(null);
  // The **watcher's** in-flight "take Overwatch" flow: set
  // the moment a watcher answers a move-checkpoint prompt with "take", cleared once the sibling
  // reaction attack's resolution acks (or the watcher cancels out of targeting before firing). This
  // is deliberately NOT an immediate `answerPrompt` — the interleaving contract (`haltable-movement`
  // Gotcha "the take is a sibling resolution, not an answer payload") requires the full existing
  // Overwatch attack intent (`request_attack {reaction:true}`) to run to completion FIRST, against
  // the still-un-moved mover, and only THEN re-submit the recorded "take" so the parked move can
  // finally re-gather and apply. `fired` distinguishes "armed, not yet clicked" (cancelling here is
  // an honest decline) from "already sent the shot" (only the shot's own ack, not an aim clear,
  // should complete the round-trip — `fireAt`'s skirmish branch always nulls `aim` right after
  // sending, well before any server ack, so a naive aim-transition watch would misfire a decline).
  type OverwatchTakeFlow = { promptId: string; intent: EncounterIntent; watcherId: string; moverId: string; fired: boolean };
  const [overwatchTake, setOverwatchTake] = useState<OverwatchTakeFlow | null>(null);
  // The shooter's board pick of where a Covering Fire shot lands along the mover's path
  // (`landing-zone-full-automation` Q5): set by the offer's "Pick where along the path", cleared when
  // a path cell is clicked (answering the offer with that cell), on Cancel/Esc (the offer's window
  // shows again), or when the offer is no longer held (answered from another client).
  const [splitPick, setSplitPick] = useState<SplitPick | null>(null);
  useEffect(() => {
    if (splitPick && !encounter.hasPrompt(splitPick.promptId)) setSplitPick(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounter.prompt, splitPick]);
  // Ack: once `fireAt` has actually sent the sibling reaction attack (`overwatchTake.fired`), watch
  // the session resolution feed for OUR OWN resolution to land — that's the shot completing (Brace
  // round-trips on the mover and all).
  //
  // **Do NOT match `res.actorId` against `encounter.actorId` here.** `res.actorId` is NOT the firing
  // client's own identity (the socket's bound slot) — it is the ATTRIBUTION slot
  // (`resolutionAttribution`, `backend/src/encounterRoom.ts`), not an identity we
  // can compare against our own. When the watcher is a GM-controlled NPC with no
  // `controllerSlotId`, attribution for the Brace resolution its own shot triggers falls back to
  // `senderSlot` — the ANSWERING PLAYER — so `res.actorId` is the player's slot, never `"gm"`, and
  // this comparison against `encounter.actorId` never matches on the GM's own client — the `take` is
  // then never re-submitted and the parked move never re-gathers. The
  // question this effect actually needs answered is "did OUR shot resolve," which the resolution's
  // own recorded attacker answers regardless of who it's attributed to on the wire — so this reads
  // {@link resolutionAttacker} (`browserMatchHandle.ts`, reused rather than re-derived) against
  // `overwatchTake.watcherId`, the watcher COMBATANT id captured at `startOverwatchTake` time, via
  // the exported pure predicate {@link overwatchTakeAckMatches} (pinned in
  // `overwatchTakeAck.test.ts`).
  // Re-submits the recorded "take" on the ORIGINAL move-checkpoint prompt — captured into
  // `overwatchTake` at `startOverwatchTake` time (`promptId` + `intent`), never re-read off
  // `encounter.prompt`: under the id-keyed prompt
  // map, the sibling Brace this same shot can raise (a cross-actor, GM-backstop-routed prompt) is
  // free to become the new HEAD while the checkpoint is still held-but-not-head — the old
  // `encounter.prompt?.promptId === overwatchTake.promptId` guard would then fail and silently drop
  // the take (the original defect). {@link RemoteEncounterController.answerPromptById} answers the
  // checkpoint by ITS OWN id regardless of what the current head is; it no-ops if the id is no
  // longer held (a genuinely stale/superseded prompt), mirroring `packages/ai/src/promptPolicy.ts`'s
  // reference shape — it captures `const prompt = obs.prompt` into a local *before* resolving the
  // sibling attack and re-submits from that local rather than re-reading a mutable "current prompt"
  // (`:284-301`); the two clients are meant to agree on this contract.
  useEffect(() => {
    if (!overwatchTake?.fired) return;
    const res = encounter.latestResolution;
    if (overwatchTakeAckMatches(res, overwatchTake.watcherId)) {
      encounter.answerPromptById(overwatchTake.promptId, OVERWATCH_TAKE_OPTION);
      setOverwatchTake(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounter.latestResolution?.seq]);
  // Decline: the SkirmishChooser's × is the take's one explicit decline, re-submitted by the
  // captured prompt id like the ack above.
  const declineOverwatch = () => {
    if (!overwatchTake || overwatchTake.fired) return;
    encounter.answerPromptById(overwatchTake.promptId, OVERWATCH_DECLINE_OPTION);
    setOverwatchTake(null);
    setAim(null);
  };
  // Any other disarm before firing (Esc, a fresh arm elsewhere, a direction prompt) is local
  // (`decisions/0189` point 4): the take ends and the held checkpoint's Take/Decline window shows
  // again; nothing reaches the server. Never fires after a successful send (`fired` guards it) —
  // `fireAt` nulls `aim` immediately on send, well before any ack.
  useEffect(() => {
    if (!overwatchTake || overwatchTake.fired) return;
    const stillArmed = aim !== null && aim.attackerId === overwatchTake.watcherId && aim.mode === "skirmish" && aim.reaction === true;
    if (!stillArmed) setOverwatchTake(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aim]);
  // A pending DIRECTION prompt supersedes any armed attack aim (mirroring how the PromptModal
  // already supersedes the attacker "waiting for {defender}" banner in
  // `RemoteEncounterController.handle`): a spatial decision answers via a board click, so any armed
  // Skirmish/Barrage/Ram/etc. aim disarms the moment one arrives — otherwise a stray click meant to
  // answer the prompt could instead fire (or be swallowed by) a stale armed attack.
  // A cell or unit pick supersedes an armed aim the same way.
  useEffect(() => {
    if (directionPromptActive || cellPick !== null || unitPick !== null) setAim(null);
  }, [directionPromptActive, cellPick, unitPick]);
  // Surface a base-edit / reducer rejection inline in the inspector (the `onError` thread the
  // shared BaseEditPanel + the inspector banner read), cleared on the next ok.
  const [inspectorError, setInspectorError] = useState<string | null>(null);
  // Camera focus request for `CombatStage` (`combat-chip-camera-focus`) — a world point plus a
  // monotonic `seq` so a REPEAT double-click on the same already-centered unit still re-fires the pan
  // (mirrors `CombatStage`'s own `ping` seq-gate). Declared here (with the other hooks, ABOVE the
  // loading early-return below) so hook order stays stable across the loading→loaded flip, same
  // reason the `promptId` effect above is pinned up here; `focusOnCombatant` (below, alongside
  // `openInspector`) is a plain function reading these, not a hook, so it's fine where it lives.
  const focusSeqRef = useRef(0);
  const [focusPoint, setFocusPoint] = useState<{ x: number; y: number; seq: number } | null>(null);
  const moveCommandType = viewMode === "edit" ? "place_token" : "move_token";
  // A hard mode-flip clears the other mode's transient arming so nothing stale survives the switch:
  // entering PLAY disarms the authoring brush / wall tool; entering EDIT drops any armed attack aim.
  // (The open windows themselves are NOT cleared — they park per-mode via `useCombatWindows`.)
  const switchViewMode = (m: "edit" | "play") => {
    setEditPlayMode(m);
    if (m === "play") {
      setBrush(null);
      setTool("select");
      setEraseMode(false);
    } else {
      setAim(null);
    }
  };


  // Hover intel (`projects/hover-intel.md`): the hovered unit's zones and stat card, and the
  // selection's zones when nothing is hovered (`projects/intel-follows-selection.md`). Anything
  // already on screen wins: an armed aim, a hovered menu row, a direction prompt, a split pick, EDIT.
  const { hoverId: hoverIntelId, off: intelOff } = useHoverIntelUnit({
    hoveredId: hoveredTokenId,
    suppressed: viewMode !== "play" || aim !== null || previewAim !== null || previewMove !== null || directionPromptActive || splitPick !== null || cellPick !== null || unitPick !== null,
  });
  const {
    overlayMode,
    reachable,
    threatRingsView,
    threatRingsAt,
    sensorReach,
    pathPreview,
    trampleTrail,
    moveForecast,
    moveStateFor,
    attackReachList,
    directedDeployWash,
    pilotBridgeWash,
    rideDismountWash,
    pendingDeploy,
    attackRange,
    attackAffected,
    attackLosExcluded,
    attackArea,
    cellPickArmed,
    targetPicking,
    outOfRangeTargetIds,
    cellSightlines,
    legalTargetIds,
    boostRange,
  } = useTargetingWashes({
    state,
    aim,
    // A hover preview is a PLAY affordance: EDIT's sheet rows paint nothing.
    previewAim: viewMode === "play" ? previewAim : null,
    previewMove,
    hoverUnitId: hoverIntelId,
    intelOff,
    selectedId,
    viewMode,
    directionPromptActive,
    prompt: encounter.prompt,
    // `encounter.actorId` directly, NOT the `actorId` const below — that one is declared further
    // down this component and would be a TDZ throw here.
    actorId: encounter.actorId,
    isGm,
    overlay,
  });
  // The override lifts the attack path's target filter: every unit is a target the GM may force.
  const targetGate = override && aim !== null && liftsTargetGate(aim) ? null : outOfRangeTargetIds;
  // A Covering Fire split pick paints the path cells in place of any attack wash: each dotted where
  // the shooter sees it, ringed where the mover would have cover (`splitPickSightlines`).
  const splitSightlines = useMemo(
    () => (splitPick && state ? splitPickSightlines({ state, pick: splitPick, ...(loadingOverlay ? {} : { overlay }) }) : null),
    [splitPick, state, loadingOverlay, overlay],
  );
  const splitKeys = useMemo(() => (splitSightlines ? new Set(splitSightlines.map((s) => s.key)) : null), [splitSightlines]);
  // The mini forecast on every legal target while an attack is armed (`target-mini-forecasts` 1.2).
  // Above the loading return with every other hook; memoized per aim and state, never per hover.
  const badgeAim = viewMode === "play" ? aim : null;
  const targetBadges = useTargetMiniForecasts({
    state,
    overlay: loadingOverlay ? undefined : overlay,
    aim: badgeAim,
    targetIds: badgeAim ? legalTargetIds : null,
    viewer: { role: session.role ?? undefined, viewerSlot: encounter.actorId },
  });

  // Commit an armed AREA attack at a cell (the AoE origin) — the cell-target twin of `fireAt`'s unit
  // click. The resolver fans the weapon's template around this cell and resolves against everyone it
  // catches. Only the stage's area-attack click path reaches this (gated on `attackArea`) — for a
  // Skirmish/Barrage weapon that's always a bare cell (a token click fires directly at the unit
  // instead); for a cell-pick throw (`activate_cell`, below) `cellPickArmed` also routes an in-range
  // OCCUPIED cell's click here — RAW allows the Blast
  // center on a cell a unit occupies, so the clicked cell is a legal site either way.
  const onCommitCell = useCallback(
    (cell: SquareCell) => {
      if (state && cellPick) {
        const optionId = cellPickAnswerAt(state, cellPick, cell);
        if (optionId === null) return;
        if (cellPickNeedsConfirm(cellPick)) setStagedCell({ promptId: cellPick.promptId, optionId, cell });
        else encounter.answerPromptById(cellPick.promptId, optionId);
        return;
      }
      if (state && splitPick) {
        const answer = splitAnswerAt(state, splitPick, cell);
        if (answer === null) return;
        encounter.answerPromptById(splitPick.promptId, answer);
        setSplitPick(null);
        return;
      }
      if (!state || !aim) return;
      // A cell-pick deploy/throw (`activate_cell` — Smoke/HEX Charges to a point,
      // a throwable deployable): the clicked cell — bare OR occupied — is the
      // `RequestActivate.site` the `for_each origin:"site"` overlay fans its template around. Fires
      // through the SAME enforced activate rail (`buildActivateIntent` with `site`) the CLI's
      // `activate --site x,y` uses; the server stays the wall on placement legality (an illegal site
      // is a rejected no-op).
      if (aim.mode === "activate_cell") {
        const actor = state.combatants[aim.attackerId];
        if (!actor) return;
        // The site must sit inside the action's reach wash, mirroring the server's
        // `checkActivateSiteRange` — the defensive twin of the skirmish
        // branch's own out-of-range guard below. `attackRange === null` ⇒ the action declares no
        // reach ⇒ ungated, exactly as it means on the attack path.
        if (attackRange && !attackRange.has(squareKey(square(cell.x, cell.y)))) return;
        encounter.sendIntent(
          buildActivateIntent(actor.id, aim.actionId.trim(), { site: { grid: "square", x: cell.x, y: cell.y } }),
        );
        setAim(null);
        return;
      }
      // A directed multi-pick placement (`activate_sites` — Jericho's two sections, the turret's one,
      // a multi-section deployable): the clicked cell must sit inside
      // `attackRange` — which for THIS mode IS `directedDeployWash`, the legal-cell set (folded
      // into `attackRange` above) — mirroring the defensive out-of-range guards every other branch
      // here already runs. `CombatStage`'s own routing (`cellPickBypassesSelect`) already tries to
      // keep an illegal press on a TOKEN from reaching here at all; this is the wall for a bare illegal
      // cell, which `attackArea` routes here directly with no token in the way.
      if (aim.mode === "activate_sites") {
        const actor = state.combatants[aim.attackerId];
        if (!actor) return;
        if (attackRange && !attackRange.has(squareKey(square(cell.x, cell.y)))) return;
        const picked: WireCell = { grid: "square", x: cell.x, y: cell.y };
        const nextDraft = appendDirectedDeployPick(aim, picked);
        // D1 — nothing is sent until the draft is complete: an incomplete draft just re-arms with the
        // grown `picks` (the wash + panel recompute off the new `aim` on the next render); only the
        // FINAL pick sends the one `request_activate` carrying every cell, one atomic resolution.
        if (directedDeployDraftComplete(nextDraft)) {
          encounter.sendIntent(buildActivateIntent(actor.id, aim.actionId.trim(), { sites: nextDraft.picks }));
          setAim(null);
        } else {
          setAim(nextDraft);
        }
        return;
      }
      // Dismount/Eject (RAW Core p.70): the clicked cell — bare
      // OR occupied — is the `at` the pilot lands at. Fires `buildDismountIntent`/`buildEjectIntent`;
      // the server is the sole wall (`checkDismountLegality`/`checkEjectLegality` — adjacency/fly-6,
      // a free Size-0.5 space, not-already-Unpiloted, and Eject's own lockout resource). Deliberately
      // NO defensive `attackRange`/out-of-range guard here, unlike every neighbouring branch — because
      // `legalPilotBridgeCells` mirrors the WHOLE wall, including position-INDEPENDENT preconditions
      // (Eject's lockout resource, already-`Unpiloted`, `pilotBlock` presence). Those reject every
      // candidate uniformly, so a locked-out mech's wash is the EMPTY set — and a guard here would
      // silently eat every click with no message and no way out of the picker (exactly the lying-
      // affordance dead-end this project exists to delete). So every clicked cell is sent, unguarded;
      // the server is the sole wall and its rejection reaches the existing `RejectionToast`
      // (`remoteEncounterController.ts`) with the real reason (e.g. "Eject is locked out until Full
      // Repair"). Do not "restore consistency" by adding the guard back — see the locked-out case
      // above.
      if (aim.mode === "dismount" || aim.mode === "eject") {
        const actor = state.combatants[aim.attackerId];
        if (!actor) return;
        const at: WireCell = { grid: "square", x: cell.x, y: cell.y };
        encounter.sendIntent(aim.mode === "eject" ? buildEjectIntent(actor.id, at) : buildDismountIntent(actor.id, at));
        setAim(null);
        return;
      }
      // Dismount (ally) / `ride_dismount` (RAW Core p.70):
      // the clicked cell — bare OR occupied — is the `at` the rider lands at; the carrier is read
      // server-side off the rider's own `Riding.partner`, never named on the wire. Fires
      // `buildRideDismountIntent`; the server is the sole wall (`checkRideDismountLegality` —
      // adjacent-to-carrier, a free Size-0.5 space, and a genuinely resolvable `Riding` pair).
      // Deliberately NO defensive `attackRange`/out-of-range guard here, mirroring the Dismount/
      // Eject branch above for the identical reason (Item A): `legalRideDismountCells` mirrors the
      // WHOLE wall, including the position-INDEPENDENT "is this rider actually Riding a resolvable
      // carrier" precondition — that rejects every candidate uniformly, so a not-currently-Riding
      // pilot's wash is the EMPTY set, and a guard here would silently eat every click with no
      // message. Every clicked cell is sent, unguarded; the server's own `RejectionToast` carries
      // the real reason.
      if (aim.mode === "ride_dismount") {
        const actor = state.combatants[aim.attackerId];
        if (!actor) return;
        const at: WireCell = { grid: "square", x: cell.x, y: cell.y };
        encounter.sendIntent(buildRideDismountIntent(actor.id, at));
        setAim(null);
        return;
      }
      if (aim.mode !== "skirmish" && aim.mode !== "barrage") return;
      // Barrage fires a cell origin only once a weapon card is armed (parity with the unit-click gate).
      if (aim.mode === "barrage" && aim.weaponId === "") return;
      const attacker = state.combatants[aim.attackerId];
      if (!attacker) return;
      // The origin cell must sit inside the reach wash, mirroring the server's
      // `checkAttackCellRange`. An out-of-range AoE origin is a wall, not a hint — don't send it (the
      // stage also blocks the click, this is the defensive twin). `attackRange === null` ⇒ ungated.
      // The GM override fires an attack at any origin.
      if (!(override && (aim.mode === "skirmish" || aim.mode === "barrage")) && attackRange && !attackRange.has(squareKey(square(cell.x, cell.y)))) return;
      const target = { kind: "cell", cell: { grid: "square", x: cell.x, y: cell.y } } as const;
      if (aim.mode === "barrage") {
        // Roll-as-you-assign: one shot, then auto-disarm the WEAPON (keep Barrage armed so the chooser
        // re-renders the remaining legal cards / closes off the synced lock).
        encounter.sendIntent(buildBarrageIntent(attacker.id, target, { weapon: aim.weaponId, profile: aim.profileId || undefined, accuracy: aim.accuracy, mounts: aim.mounts }));
        setAim(override ? null : { ...aim, weaponId: "", profileId: "", accuracy: 0 });
        return;
      }
      encounter.sendIntent(
        buildAttackIntent(attacker.id, target, { weapon: aim.weaponId || undefined, profile: aim.profileId || undefined, accuracy: aim.accuracy }),
      );
      setAim(null);
    },
    [state, aim, encounter, attackRange, splitPick, cellPick, override],
  );
  const confirmStagedCell = useCallback(() => {
    if (!liveStaged) return;
    encounter.answerPromptById(liveStaged.promptId, liveStaged.optionId);
    setStagedCell(null);
  }, [encounter, liveStaged]);
  // The cell pick's wash, click gate and section ghosts, standing in for the aim's own below.
  const cellPickWash = useMemo(() => (state && cellPick ? cellPickKeys(state, cellPick) : null), [state, cellPick]);
  const cellPickSightlines = useMemo(
    () => (cellPickWash ? Array.from(cellPickWash, (key) => ({ key, visible: true, cover: "none" as const })) : null),
    [cellPickWash],
  );
  const unitPickDim = useMemo(() => (state && unitPick ? unitPickDimmed(state, unitPick) : null), [state, unitPick]);
  const cellPickDeploy = useMemo(() => {
    if (state && cardHover && "cell" in cardHover) return { cells: [worldFor(state).key(cardHover.cell)], size: 1 };
    return state && cellPick ? cellPickGhosts(state, cellPick, liveStaged) : null;
  }, [state, cellPick, liveStaged, cardHover]);

  // Commit a pending DIRECTION prompt's answer at a clicked bare cell — the click-a-cell twin of
  // `onCommitCell`, but for a spatial `needs_decision` rather
  // than an armed AoE attack. No client-side legality check on the cell (the "UI mirrors, never
  // re-enforces" rule): the engine clamps the travel distance itself (the chosen cell is an anchor/
  // direction, not a teleport destination — `travelDestination` walks at most the mover's own
  // `dist`), so any cell is a valid click here. `answerPromptDirection` is itself a no-op if the
  // prompt already cleared (a race with the resolution broadcast) — nothing to guard locally.
  const onCommitDirection = useCallback(
    (cell: SquareCell) => {
      encounter.answerPromptDirection({ grid: "square", x: cell.x, y: cell.y });
    },
    [encounter],
  );

  // The tile-by-tile route a token travels when its position changes under us — used by the stage to
  // *animate* a remote move (a GM/other player's `move_token` plays as a step-by-step travel, not a
  // teleport). Resolves the cheapest legal path from the token's prior cell `from` to its new cell
  // `to` against the current board (the mover is excluded from its own occupancy, so its new resting
  // cell never blocks the route), reusing the SAME `movePathAnchors` the drag highlight does — so the
  // animation traces exactly the path that was previewed. `null` (⇒ the stage settles instantly) for
  // a teleport/out-of-range jump or outside PLAY (EDIT free-places, no pathed movement).
  // The Director's move planner: resolve a move's tile route (the same
  // `movePathAnchors` the drag highlight traces), then project each anchor to its footprint-top-left
  // world point — the exact points a `MoveBeat` slides the token through. `null` (⇒ a blink, never a
  // skip) for a teleport / out-of-range jump or outside PLAY. The route is resolved with a budget
  // generous enough to cover the WHOLE accepted move: by the time we diff, the mover's remaining
  // budget may be 0 (a full-speed move), which would otherwise collapse a real walk to a blink — but
  // the move was already accepted as legal, so a path within its Speed always exists.
  const planMove = useCallback<MovePlanner>(
    (id, from, to) => {
      if (!state || viewMode !== "play") return null;
      const c = state.combatants[id];
      if (!c) return null;
      const turn = state.activation.turn;
      // `c.base.speed` is `undefined` for a masked hostile on a player's client (see
      // `animateMoveBudget`'s doc comment in `combatCells.ts` for why that's safe to
      // paper over here specifically).
      const planState =
        turn.phase === "acting" && turn.actor === id
          ? {
              ...state,
              activation: {
                ...state.activation,
                turn: { ...turn, budget: { ...turn.budget, move: animateMoveBudget(c.base.speed, gridExtent(state)) } },
              },
            }
          : state;
      // A move that carried its own route (a Trampler's crossing) walks that route, not the engine's.
      const committed = encounter.committedMovePath(id);
      const world = worldFor(state);
      const anchors =
        committed !== undefined && committed.length >= 2 && world.equals(committed[0]!, from) && world.equals(committed[committed.length - 1]!, to)
          ? committed
          : (movePathAnchors(planState, { ...c, pos: asSquareCell(from) }, asSquareCell(to)) ?? draggedRoute(state, c, { from, to }));
      if (!anchors) return null;
      return anchors.map((a) => {
        const box = footprintBox(asSquareCell(a), c.base.size);
        return { x: box.minX * CELL, y: box.minY * CELL };
      });
    },
    [state, viewMode, encounter],
  );

  // The central animation sequencer: the map renders the lagging `presentation` it advances toward
  // authoritative one beat at a time. Only the MAP reads presentation — the turn
  // tracker, action bar, and chat keep reading authoritative `state` and stay live.
  const {
    presentation,
    registerNode,
    unregisterNode,
    getNode,
    setTicker,
    commitLocalMove,
    watchSettle,
    noteMoveHeld,
    floats,
    onFloatDone,
    projectiles,
    onProjectileDone,
    impacts,
    onImpactDone,
    focus: chainFocus,
    hold,
    holdAhead,
    isAnimating,
    skip,
  } = usePresentationDirector(
      { authoritative: state, latestResolution: encounter.latestResolution, connStatus: encounter.connStatus },
      planMove,
      CELL,
    );


  const { undoRef, redoRef, editKbRef, railKeyRef, holdMeasureRef, undoRedoSeq, bumpUndoRedoSeq } = useCombatKeyboard({
    esc,
    selectedId,
    setSelectedId,
    setBrush,
    setTool,
    pickBrush,
  });
  // The Measure tool (`useMeasureTool`): the rail's ruler and hold-M, shared live with the table.
  const measure = useMeasureTool({
    esc,
    state,
    overlay: loadingOverlay ? undefined : overlay,
    send: encounter.sendMeasure,
    remote: encounter.measures,
    nameOf: slotNameOf,
  });
  holdMeasureRef.current = { start: measure.startHold, end: measure.endHold };
  // The Inspect tool (`useInspectTool`): reads the hovered cell the Map key reads.
  const inspect = useInspectTool({ esc, hoverCell: hoverInfoCell });

  // Auto-close a per-unit inspector whose combatant has been removed/deleted. Runs on each state
  // change; `close` is a no-op (same registry ref) when
  // there's nothing to drop, so this is stable even though it can run every render.
  useEffect(() => {
    if (!state) return;
    for (const wid of windows.registry.order) {
      const cid = inspectorCombatantId(wid);
      if (cid && !state.combatants[cid]) windows.close(wid);
    }
  }, [state, windows.registry, windows.close]);

  // Drop the armed aim if its attacker leaves the encounter (deleted/removed) — an aim belongs to a
  // specific combatant.
  useEffect(() => {
    if (state && aim && !state.combatants[aim.attackerId]) setAim(null);
  }, [aim, state]);

  // Visualize every authoritative roll on the global 3D dice overlay. When the server broadcasts
  // a resolution (attack / save /
  // structure-stress table check), the recorded steps land in the session feed; we throw the dice
  // they rolled onto their recorded faces. Keyed on the monotonic `seq` so an identical re-attack
  // still re-throws, and the initial feed state on mount is not replayed. Visualization-only — it
  // never dispatches a command or touches state; the overlay's baked-correction replay drives the
  // die from `res.steps`' own recorded dice, no second RNG.
  //
  // The throw is also **registered** under this resolution's `seq`, which is how the board's
  // reveal gate finds it: `usePresentationDirector` — declared
  // above, so its effect runs BEFORE this one — holds the damage/MISS float until the promise
  // registered here resolves. Registration happens for EVERY observed resolution, the first-observe
  // and no-dice cases included (an already-resolved promise), because a resolution that is observed
  // but never registered is the one shape that leaves the board waiting out the full ceiling.
  const lastDiceSeqRef = useRef<number | null>(null);
  /**
   * Whether this effect has run at all yet. The "don't replay a pre-existing resolution on mount"
   * rule needs to know what existed AT MOUNT, and `lastDiceSeqRef === null` is not that question:
   * on a fresh join the resolution log is empty, so the ref is still null when the first LIVE
   * resolution arrives, and that resolution was being skipped as if it were history. The effect
   * ran, `throwSteps` never fired, and the very first attack of every session rolled no dice at
   * all — no tumble and no readout. Found by an e2e probe that counted zero readouts after a real
   * attack (`dice-land-before-reveal`); the mount pass now seeds the watermark explicitly, so
   * "pre-existing" means "was there when we mounted" rather than "is the first one we saw."
   */
  const diceMountedRef = useRef(false);
  useEffect(() => {
    const res = encounter.latestResolution;
    if (!diceMountedRef.current) {
      diceMountedRef.current = true;
      lastDiceSeqRef.current = res?.seq ?? null; // whatever is already here IS the history
      // Still register it: a gate that ingested this same resolution must not be left holding for
      // dice that were deliberately never thrown.
      if (res) {
        registerResolutionThrow(res.id, Promise.resolve());
        registerResolutionThrow(segmentThrowKey(res.id, 0), Promise.resolve());
      }
      return;
    }
    if (!res || lastDiceSeqRef.current === res.seq) return;
    lastDiceSeqRef.current = res.seq;
    // A chained resolution throws only its primary action's dice here (`id#0`, the board's first
    // gate); the board throws each later link's dice, and a held roll's, when it reaches them
    // (`usePresentationDirector`, `chained-resolution-sequence` 2.1 and 3.1). The bare `id` is what
    // COMMS holds the card on: the primary throw, plus every hold's release when the chain holds, so
    // the chain card lands after the last held roll rather than spoiling it (3.3).
    const segments = chainSegments(res.steps, res.holds ?? NO_HOLDS);
    const primary = segments[0]!;
    const primaryThrow = primary.hold === undefined ? throwSteps(primary.steps) : Promise.resolve();
    if (primary.hold === undefined) registerResolutionThrow(segmentThrowKey(res.id, 0), primaryThrow);
    const holds = segments.filter((s) => s.hold !== undefined);
    if (holds.length === 0) registerResolutionThrow(res.id, primaryThrow);
    else registerResolutionThrow(res.id, Promise.all([primaryThrow, ...holds.map((s) => holdOpens(holdKey(res.id, s.index)))]).then(() => {}), { unbounded: true });
  }, [encounter.latestResolution]);

  // COMBAT.app is the ONLY thing that throws combat-resolution dice, so while it is closed no
  // throw is ever coming for a resolution — and COMMS.app is still showing that resolution's
  // result card. Announcing the mount is what lets the card tell "wait, dice are on their way"
  // from "nothing is going to roll," rather than holding every card for the full ceiling
  // whenever the board happens to be closed.
  useEffect(() => markResolutionThrowSource(), []);

  // The armed action is a layer-3 Esc entry while an aim is armed (not re-registered as the aim
  // changes), so Esc disarms it before any menu screen or window.
  useEscEntry(esc, { layer: "armed", id: "armed-action", active: aim !== null, onDismiss: cancelAim });
  useEscEntry(esc, { layer: "armed", id: "split-pick", active: splitPick !== null, onDismiss: () => setSplitPick(null) });
  useEscEntry(esc, { layer: "armed", id: "staged-cell", active: liveStaged !== null, onDismiss: () => setStagedCell(null) });
  // A board pick's way out is the old prompt window's ✕: a local dismiss, offered only where the
  // window's was (the viewer's own action raised it, `promptIsRequired`). Neither pick has a decline.
  const boardPickDismissible = (cellPick !== null || unitPick !== null) && encounter.prompt !== null && !promptIsRequired(encounter.prompt);
  useEscEntry(esc, { layer: "armed", id: "board-pick", active: boardPickDismissible && liveStaged === null, onDismiss: () => encounter.dismissPrompt() });
  // The expanded unit panel is a layer-5 member like a window: Esc collapses it before it deselects.
  useEscEntry(esc, { layer: "window", id: "unit-panel-sheet", active: sheetOpen && viewMode === "play", onDismiss: () => setSheetOpen(false) });
  // THE ACTION ASKS (rendered below, docked at the map's bottom centre, `website-shell` 4.2) shows
  // while a prompt wants it and no take flow or path pick has taken that prompt over. It is a
  // layer-5 Esc entry only while its prompt is dismissible: Esc runs the modal's Cancel.
  // A prompt the table waits on holds no entry, so Esc passes it by (`decisions/0189` point 6).
  const promptDockShown =
    encounter.prompt !== null &&
    promptWindowWanted &&
    overwatchTake?.promptId !== encounter.prompt.promptId &&
    splitPick?.promptId !== encounter.prompt.promptId;
  useEscEntry(esc, {
    layer: "window",
    id: "action-asks",
    active: promptDockShown && encounter.prompt !== null && !promptIsRequired(encounter.prompt),
    onDismiss: () => encounter.dismissPrompt(),
  });
  // A menu-armed Full Tech ("Run both"): once `fireAt` sends a pick and re-arms option-less, arm
  // the next queued pick, or clear the aim after the last (`advanceFullTechQueue`). A layout effect,
  // so the option-less aim between the two picks never paints (the menu would twitch out and back).
  useLayoutEffect(() => {
    const next = advanceFullTechQueue(aim);
    if (next !== undefined) setAim(next);
  }, [aim]);

  // The docked menu's state for `c`, or `null` when it does not open on `c`. The same ownership read
  // as `canControl` below (declared after the loading return, so restated here from the same inputs).
  const menuStateOf = (c: Combatant): CommandMenuState | null => {
    const menu =
      viewMode === "play" && state !== null
        ? commandMenuStateFor({
            combatant: c,
            state,
            canControl: session.role === "gm" || controlsCombatant(c, encounter.actorId ?? ""),
            viewer: { actorId: encounter.actorId, isGm: session.role === "gm" },
          })
        : null;
    // The override opens every unit's menu enabled, on its turn or not.
    return menu !== null && override ? OVERRIDE_MENU_STATE : menu;
  };
  const menuMayShow = (c: Combatant): boolean => menuStateOf(c) !== null;
  // The one selection is the menu's only source: the selected unit, while the menu may show it.
  // Selecting another unit switches it, deselecting (an empty-board click, the Esc floor, a click on
  // a unit the menu does not open on) hides it. Esc steps the menu back a screen (layer 4) and hides
  // it only at the floor (`decisions/0189`).
  const shownMenu = state ? commandMenuUnitFor({ state, selectedId, stateOf: menuStateOf }) : null;
  // The turn passing to a unit the menu may show selects it when nothing else is selected, so the
  // menu shows the active unit unasked (Phase 2.3) through the same one selection.
  const actingId = state?.activation.turn.phase === "acting" ? state.activation.turn.actor : null;
  useEffect(() => {
    if (actingId === lastActorRef.current) return;
    lastActorRef.current = actingId;
    const pick = selectOnTurnStart({ actorId: actingId, selectedId, mayShow: (id) => state?.combatants[id] !== undefined && menuMayShow(state.combatants[id]!) });
    if (pick) setSelectedId(pick);
    // Only a change of actor selects; `state` and `selectedId` are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actingId]);
  // An armed aim whose attacker can no longer act (another unit acting, its turn over, out of the
  // viewer's control) is stale: clear it, as arming is disabled off-turn (`staleAim`).
  const aimAttacker = aim && state ? state.combatants[aim.attackerId] : undefined;
  const aimCanAct = aimAttacker && state ? aimAttackerCanAct({ state, attacker: aimAttacker, canControl: session.role === "gm" || controlsCombatant(aimAttacker, encounter.actorId ?? ""), viewer: { actorId: encounter.actorId, isGm: session.role === "gm" } }) : false;
  const aimSeenRef = useRef<AimActSeen | null>(null);
  useEffect(() => {
    const seen = aimSeenRef.current;
    aimSeenRef.current = aim ? { attackerId: aim.attackerId, canAct: aimCanAct } : null;
    if (staleAim({ aim, seen, canAct: aimCanAct || override })) setAim(null);
    // Only the attacker's ability to act changing can stale the aim; `aim` is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aim?.attackerId, aimCanAct, override]);

  // Esc → Select: an armed authoring brush/tool is a layer-3 Esc entry, so Esc disarms it back to
  // Select (and clears the erase toggle). Registered while a tile brush or the wall/room tool is up.
  // Only ever true in EDIT (PLAY resets brush/tool), so it's inert on the fight surface.
  const authoringArmed = brush !== null || tool !== "select";
  useEscEntry(esc, {
    layer: "armed",
    id: "authoring-tool",
    active: authoringArmed,
    onDismiss: () => {
      setBrush(null);
      setTool("select");
      setEraseMode(false);
    },
  });

  // Esc → drop Draw: an armed draw tool is a layer-3 Esc entry, disarming it back to `null`.
  // Mode-independent (no `viewMode`/`isGm` gate) — Draw is armed regardless.
  const drawArmed = drawTool !== null;
  useEscEntry(esc, { layer: "armed", id: "draw-tool", active: drawArmed, onDismiss: () => setDrawTool(null) });

  // One armed thing on the board (`decisions/0189` point 5, `armedSeam.ts`): whichever layer-3
  // member arms, the others disarm. The terrain rail and Draw already exclude each other
  // (`useAuthoringToolbar`); this adds the aim, which many surfaces arm. Disarming an Overwatch
  // take's aim this way is local (the take effect above).
  const boardArmed: BoardArmed = { aim: aim !== null, draw: drawArmed, terrain: authoringArmed, measure: measure.armed, inspect: inspect.armed };
  const boardArmedRef = useRef(boardArmed);
  useEffect(() => {
    const prev = boardArmedRef.current;
    boardArmedRef.current = boardArmed;
    for (const kind of disarmOnArm(prev, boardArmed)) {
      if (kind === "aim") setAim(null);
      if (kind === "draw") setDrawTool(null);
      if (kind === "terrain") {
        setBrush(null);
        setTool("select");
        setEraseMode(false);
      }
      if (kind === "measure") measure.setArmed(false);
      if (kind === "inspect") inspect.setArmed(false);
    }
    // The five flags are the whole input; `boardArmed` is rebuilt from them every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardArmed.aim, boardArmed.draw, boardArmed.terrain, boardArmed.measure, boardArmed.inspect]);

  const { sitrepZones, sitrepClearanceTotal } = useSitrepZones(state);
  // Each token's turn state + vitals, projected for this viewer (`tokenChromeFor`). Read off the
  // same lagging presentation the board draws positions from, so a damage float and the HP bar
  // it lands on change together.
  const chromeState = presentation ?? state;
  const tokenChrome = useMemo(
    () => (chromeState ? tokenChromeFor(chromeState, perUnitState(chromeState), { viewerSlot: encounter.actorId ?? null, role: isGm ? "gm" : "player" }) : {}),
    [chromeState, encounter.actorId, isGm],
  );
  // The Inspect readout: the overlay's pieces once per terrain map while the tool is armed, and the
  // hovered cell's reading off the same projected state and vitals the board draws.
  const inspectBoard = state ? gridExtent(state) : null;
  const inspectPieces = useMemo(
    () => (inspect.armed && state && inspectBoard ? terrainMechanicsPieces(state.terrain, inspectBoard) : undefined),
    // `inspectBoard` is rebuilt every render; its two numbers are the input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inspect.armed, state?.terrain, inspectBoard?.width, inspectBoard?.height],
  );
  const inspectData = useMemo(
    () =>
      inspect.cell && chromeState && inspectBoard
        ? inspectCell({ state: chromeState, cell: inspect.cell, board: inspectBoard, chrome: tokenChrome, zones: sitrepZones, pieces: inspectPieces })
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inspect.cell, chromeState, tokenChrome, sitrepZones, inspectPieces],
  );

  // Blank ONLY on the honest first load — never had state for this encounter id yet.
  // `encounter.loading` means exactly that
  // (see `useRoomController`'s doc), so a RECONNECT no longer trips this: `useRoomController`
  // retains the last-welcomed state across a controller swap, so `state` stays non-null and
  // `loading` stays false through a drop — the board below stays mounted (Pixi's renderer,
  // WebGL context and compiled shaders survive with it) and the app-wide link pill
  // (`lib/linkHealth.tsx`, rendered by `RequireAuth`) says why nothing syncs. `!state` stays alongside `loading` only for TypeScript's narrowing below (the two
  // are equivalent by construction now — `useRoomController` never reports one without the
  // other) — not because they can genuinely diverge.
  //
  // NOTE for whoever adds the next hook here: `terraformer-encounter`'s `sitrepZones` /
  // `sitrepClearanceTotal` memos land directly above this return (merged 2026-08-09), and like
  // every hook in this component they MUST stay above it — see the hook-order comments at the
  // top of the file. This early return is the boundary; nothing hook-shaped may go below it.
  if (encounter.loading || !state) {
    return (
      <div className="combat-app combat-app-loading">
        <div className="combat-app-loading-panel">
          <span className="combat-app-loading-dot" aria-hidden />
          LINKING TO ENCOUNTER…
          {/* The manual control on the FIRST-LOAD panel. This branch is the one place a
              retained board can't help: state has never arrived, so there is nothing to render and
              nothing for the pill to sit on. Shown only once the automatic fast-retry budget is
              spent — before that it would just invite mashing a button while a healthy 1s backoff
              is already in flight. */}
          {encounter.linkLost && (
            <button type="button" className="lvtt-relink-btn" onClick={encounter.reconnect}>
              RELINK NOW
            </button>
          )}
        </div>
        <CombatComms>
          <CommsApp answerDecision={encounter.answerDecision} onHoverDecisionOption={onHoverDecisionOption} />
        </CombatComms>
      </div>
    );
  }

  const extent = gridExtent(state);
  const actorId = encounter.actorId ?? "";
  // Whether THIS encounter is the table's scene (not a GM preview) — gates the GM's END COMBAT.
  const isActiveFight = session.activeEncounterId === encounterId;
  // This encounter's commit files its one undo here (`encounterCommitScope`); the GM's
  // "Undo commit" walks it back.
  const commitScope = encounterCommitScope(encounterId);
  const commitUndo = roster.undoState(commitScope);
  // The fight is over and the roster records no commit for it: the rail offers COMMIT WEAR.
  const fightOverUncommitted = isCombatOver(state) && roster.state !== null && roster.state.encounterCommits?.[encounterId] === undefined;
  // The tools issue GM-only encounter commands; inert until we're connected with an identity.
  const toolsDisabled = encounter.connStatus !== "connected" || !encounter.actorId;
  // Feed the keyboard-scheme gating ref (read by the once-subscribed key listener above).
  editKbRef.current = { viewMode, toolsDisabled };
  // The board tool rail's view of the armed state (`BoardToolRail.tsx`). Its keys arm through the
  // same entries its buttons do.
  const railCtx: RailContext = {
    drawTool,
    pickDrawTool,
    drawColor,
    setDrawColor,
    penMode,
    terrainArmed: authoringArmed,
    disarmTerrain: () => {
      setBrush(null);
      setTool("select");
      setEraseMode(false);
    },
    measureArmed: measure.armed,
    setMeasureArmed: measure.setArmed,
    inspectArmed: inspect.armed,
    setInspectArmed: inspect.setArmed,
    build: {
      toolbar: authoring,
      state,
      actorId,
      disabled: toolsDisabled,
      // A closure: `dispatch` is declared further down, and the rail only calls it on a click.
      dispatch: (command) => dispatch(command),
    },
    // The retired top bar's controls (`website-shell` 4.3). Closures over `doUndo`/`doRedo`, which are
    // declared further down; the rail only calls them on a click.
    table: {
      canUndo: encounter.undoState.canUndo,
      canRedo: encounter.undoState.canRedo,
      undoLabel: encounter.undoState.undoLabel,
      redoLabel: encounter.undoState.redoLabel,
      undo: () => doUndo(),
      redo: () => doRedo(),
    },
    view: viewCtx,
    scene: isGm
      ? {
          open: scenePanel,
          toggle: (panel) => setScenePanel((cur) => (cur === panel ? null : panel)),
          // Closures over `dispatch` and `canControl`, declared further down; the panels call them later.
          body:
            scenePanel === "units" ? (
              <UnitsBody
                state={state}
                actorId={actorId}
                selectedId={selectedId}
                disabled={toolsDisabled}
                onSelect={setSelectedId}
                canControl={(c) => canControl(c)}
                dispatch={(command) => dispatch(command)}
                onAddMech={() => openPicker("mech")}
                onAddPilot={() => openPicker("pilot")}
                onAddEnemy={() => openPicker("npc")}
              />
            ) : scenePanel === "map-setup" ? (
              <MapSetupBody extent={extent} state={state} actorId={actorId} disabled={toolsDisabled} dispatch={(command) => dispatch(command)} />
            ) : null,
        }
      : undefined,
    gm: isGm
      ? {
          mode: viewMode,
          setMode: switchViewMode,
          ...(viewMode === "play" ? { override: { on: overrideOn, toggle: () => setOverrideOn((o) => !o) } } : {}),
          // "Undo commit" walks back this encounter's last commit while the roster still holds it.
          undoCommit: commitUndo.canUndo ? () => roster.undo(commitScope) : undefined,
          // END COMBAT opens the commit panel (`EndCombatPanel`), only while this encounter is the
          // table's scene and the fight is still live; ending switches the table to Narrative
          // (`decisions/0203` point 2). A preview has none.
          endCombat: isActiveFight && !isCombatOver(state) ? () => setEndPanelOpen(true) : undefined,
          // Once the fight is over, COMMIT WEAR takes END's place while no commit is recorded for
          // it, so an end without a commit (the Director, the CLI, End without committing) never
          // strands the wear. Not gated on the table scene: an ended fight has left the table, so the
          // GM commits from a preview (*Peek* on ENCOUNTERS).
          commitWear: fightOverUncommitted ? () => setEndPanelOpen(true) : undefined,
          // The room keeps both (`chained-resolution-sequence` 4.4), so every board and a late joiner agree.
          holds: {
            holdNext: encounter.sequence?.holdNext ?? false,
            toggleHoldNext: () => encounter.sendHoldNext(!(encounter.sequence?.holdNext ?? false)),
            autoHold: encounter.sequence?.autoHold ?? true,
            toggleAutoHold: () => encounter.sendAutoHold(!(encounter.sequence?.autoHold ?? true)),
          },
        }
      : undefined,
  };
  // One bare-key dispatcher: the rail's tools, then the VIEW toggles. Their keys never overlap
  // (`viewRows.test.ts`). View toggles work while disconnected; they touch nothing on the server.
  railKeyRef.current = (key) => {
    const action = toolsDisabled ? undefined : railKeyAction(key, viewMode);
    if (action) {
      action.press(railCtx);
      return true;
    }
    const row = viewRowForKey(key, viewMode);
    if (!row) return false;
    row.toggleOf(viewCtx).toggle();
    return true;
  };

  // The MAP reads the lagging presentation (the Director's shadow); everything else reads
  // authoritative `state`. `presentation` is null only for the first render before the seed effect
  // runs — fall back to `state` so the map never flashes empty.
  const mapState = presentation ?? state;
  const combatants = Object.values(mapState.combatants);
  // What the board's token art resolves against — the live roster (uploaded mech/pilot art), NPC
  // roles, and this viewer's disclosure (a masked hostile shows the bare ring).
  const tokenImageCtx = buildTokenImageCtx({ roster: roster.state, compendium, state: mapState, viewer: { role: session.role ?? undefined, viewerSlot: encounter.actorId } });
  // The deploy picker's mech source — the roster pilots, flattened to rows in the panel.
  const pilots = roster.state ? Object.values(roster.state.pilots) : [];
  // Affordance mirror of the server ownership gate (`canIssueEncounter`): a GM commands anything;
  // a player only its own combatants. Never re-enforced — the server stays the wall.
  const canControl = (c: Combatant): boolean =>
    session.role === "gm" || controlsCombatant(c, actorId);
  // A combatant armed for first-placement: selected, unplaced, and controllable. The stage drops it
  // on the next empty-cell click. `null` when the selection is already placed or not controllable.
  const selected = selectedId ? (state.combatants[selectedId] ?? null) : null;
  // The command menu's unit and state (`shownMenu`, the selection while the menu may show it).
  const menuUnit = shownMenu?.unit ?? null;
  const menuState = shownMenu?.menuState ?? null;
  // Targeting mode (2.5): the banner for the armed aim, when it picks a unit.
  // A multi-target weapon's cap while its Skirmish is armed (`multiTarget.ts`).
  const pickCap = multiTargetCap({ state, aim, overlay: loadingOverlay ? undefined : overlay });
  const targeting = viewMode === "play" ? targetingBanner({ state, aim, legalCount: legalTargetIds?.length ?? 0, ...(attackRange ? { legalCells: attackRange.size } : {}) }) : null;
  // The seam a targeting-mode hover forecast reads (2c.10): `{ aim, targetId }` for the hovered
  // legal target while an aim is armed.
  // A touch-expanded target (2.1) stands in for the hover a finger cannot give.
  const touchExpandedId = touchExpanded && touchExpanded.aim === aim ? touchExpanded.targetId : null;
  const hoverTarget = targeting ? armedHoverTarget({ aim, hoveredId: touchExpandedId ?? hoveredTokenId, legalTargetIds: legalTargetIds ?? null }) : null;
  // The hover forecast's intent: the same `request_attack` a click on that target sends (`fireAt`).
  const forecastIntent = hoverTarget ? attackIntentForAim(state, hoverTarget.aim, hoverTarget.targetId) : null;
  // The target whose full card is open: its mini forecast badge hides while the card shows.
  const expandedTargetId = forecastIntent && hoverTarget ? hoverTarget.targetId : null;
  // The rings on the units the washed action can legally target (2.4 hover, 2.5 armed).
  const targetRings: TargetRingSpec | null = unitPick
    ? { ids: unitPick.ids, kind: "attack", phase: "armed" }
    : cardHover && "unitId" in cardHover
      ? { ids: [cardHover.unitId], kind: "attack", phase: "hover" }
      : viewMode === "play" && legalTargetIds && (aim ? targeting : previewAim)
      ? { ids: legalTargetIds, kind: targeting?.kind ?? (overlayMode === "tech" ? "tech" : "attack"), phase: aim ? "armed" : "hover" }
      : null;
  // The unit panel: the anchor's unit as this viewer may see it (`unitPanelFor`).
  const menuUnitId = menuUnit && menuState ? menuUnit.id : null;
  const unitPanel =
    viewMode === "play"
      ? unitPanelFor({
          // While a held roll is ahead, the read-only vitals read the board's presentation, so the
          // panel doesn't announce what the table has not rolled yet (`chained-resolution-sequence` 3.3).
          state: holdAhead && presentation !== null ? presentation : state,
          // The expanded sheet stays on the selection: a hover previews nothing.
          anchor: { hoverId: sheetOpen ? null : hoverIntelId, selectedId },
          viewer: {
            viewerSlot: encounter.actorId,
            role: session.role ?? undefined,
            identityOf: (c) => unitSheetIdentity({ combatant: c, compendium, pilots: roster.state?.pilots ?? {}, slots: session.slots }),
          },
        })
      : null;
  const placing = selected && selected.pos == null && canControl(selected) ? selected : null;
  // GM-gate authoring through the same `disabled` thread the GRID/UNITS tools use: the tile brush is
  // only live, and the wall tool only armed, when the tools are enabled — a non-GM / disconnected
  // client falls back to a null brush / the select tool, so it can never paint a tile or a wall.
  const liveBrush = toolsDisabled ? null : brush;
  const liveTool: CombatTool =
    toolsDisabled && (tool === "wall" || tool === "room" || tool === "object") ? "select" : tool;
  // The OBJECTS tool's currently-configured pick, threaded into the stage's click-to-place gesture
  // (`objectPlacementCommand`). Cheap to rebuild each render — the stage only reads it on a click.
  const objectPlan: ObjectAuthoringPlan = {
    preset: objectPreset,
    sectionCount: objectSectionCount,
    armor: objectArmor,
    orientation: objectOrientation,
    height: objectHeight,
  };

  // A Skirmish shot at `picks` (one unit, or a multi-target weapon's picks). Returns `true` when it
  // kept the aim armed for a further shot of the same action, else the caller clears it.
  const fireSkirmish = (skirmish: Extract<ActionDraft, { mode: "skirmish" }>, picks: readonly string[]): boolean => {
    const aim = skirmish;
    const targetId = picks[0]!;
    const attacker = state.combatants[aim.attackerId];
    if (!attacker) return false;
    {
      // The same intent the hover forecast reads (`attackIntentForAim.ts`, which documents the
      // Overwatch `reaction` flag and the ammo / opt-in rider `choices` split). A multi-target
      // weapon's further picks ride `alsoTargets` (`multiTarget.ts`).
      encounter.sendIntent(multiTargetIntent(state, aim, picks));
      // "Take Overwatch" bookkeeping: if this shot IS the
      // armed take-flow's own shot (same watcher, same pre-aimed mover, a genuine Overwatch reaction),
      // mark it fired — the ack effect above completes the interleaving contract once this attack's
      // own resolution lands. A stray unrelated Overwatch shot (a different attacker, or this same
      // watcher firing at a DIFFERENT target) never touches the parked move's checkpoint prompt.
      if (
        overwatchTake &&
        !overwatchTake.fired &&
        aim.reaction === true &&
        attacker.id === overwatchTake.watcherId &&
        targetId === overwatchTake.moverId
      ) {
        setOverwatchTake({ ...overwatchTake, fired: true });
      }
      // Skirmish keep-aim (predicate-gated, NOT always-open): only hold the aim
      // open when THIS shot could still open an Aux rider lock (`state` here is still pre-shot — the
      // broadcast from `sendIntent` above hasn't landed yet — so the sibling Aux still reads unfired).
      // Mirrors the barrage branch's disarm-weapon-keep-aim below: clear the weapon/profile so
      // `SkirmishChooser` re-renders the rider from the synced lock, but keep Skirmish armed.
      // Otherwise fall through to the ordinary `setAim(null)`.
      //
      // `isSkirmishPrimaryFire` gates whether to even ASK `shouldKeepSkirmishAimForRider` at all
      // — that predicate's single-element `fired` simulation
      // is contracted to run on a genuinely pre-shot state (BEFORE any shot of this Skirmish has
      // landed) — once a lock is already open, this fire IS the rider's own continuation, and asking
      // again would re-flag the already-fired primary as still eligible on an Aux/Aux mount. See
      // `isSkirmishPrimaryFire`'s doc comment above.
      //
      // `isBackswingReservationFire` gates it a second way: a reservation-riding fire is a ONE-SHOT
      // free follow-up, not a genuine Skirmish primary
      // — never invite the sibling Aux rider off it, even when the reserved weapon's mount topology
      // says one is eligible. See that predicate's own doc comment.
      //
      // A multi-attack weapon (`multiAttackContinues`) keeps the aim the same way on every attack
      // but its last, primary or continuation, so the menu re-arms it for the next attack.
      // A forced shot opens no lock, so it has no rider to wait for.
      if (
        !override &&
        !isBackswingReservationFire(state, attacker.id, aim.weaponId) &&
        ((isSkirmishPrimaryFire(state, attacker.id) && shouldKeepSkirmishAimForRider(state, attacker.id, aim)) ||
          multiAttackContinues(state, attacker.id, aim))
      ) {
        const { picks: _picks, ...rest } = aim;
        setAim({ ...rest, weaponId: "", profileId: "" });
        return true;
      }
    }
    return false;
  };
  // A Barrage shot at `picks`, then disarm the WEAPON and keep Barrage armed: the chooser re-renders
  // the remaining legal cards off the synced lock and closes itself once the Barrage completes.
  const fireBarrage = (barrage: Extract<ActionDraft, { mode: "barrage" }>, picks: readonly string[]) => {
    const { picks: _picks, ...aim } = barrage;
    const shot = buildBarrageIntent(aim.attackerId, { kind: "unit", id: picks[0]! }, { weapon: aim.weaponId, profile: aim.profileId || undefined, accuracy: aim.accuracy, mounts: aim.mounts });
    encounter.sendIntent(picks.length > 1 ? { ...shot, alsoTargets: picks.slice(1) } : shot);
    // A forced shot opens no lock, so there is no Barrage to continue: the fire card stays for the
    // next weapon, picked by hand.
    setAim(override ? null : { ...aim, weaponId: "", profileId: "", accuracy: 0 });
  };
  // The armed multi-target shot's CONFIRM: fire at the picks so far (`multiTarget.ts`).
  const confirmPicks = () => {
    if (!isPickingAim(aim) || !aim.picks?.length) return;
    if (aim.mode === "barrage") return fireBarrage(aim, aim.picks);
    if (!fireSkirmish(aim, aim.picks)) setAim(null);
  };

  // Fire the armed action at a clicked target token: skirmish → `request_attack`,
  // activate → `request_activate` bound to the target. Out-of-range is warn-and-allow (no spatial
  // gate this phase), so this always sends; the authority resolves and the result card appears.
  const fireAt = (targetId: string) => {
    if (!aim) return;
    const attacker = state.combatants[aim.attackerId];
    if (!attacker) return setAim(null);
    if (aim.mode === "skirmish") {
      // A weapon that attacks several targets at a time collects its picks until the cap.
      const cap = multiTargetCap({ state, aim, overlay: loadingOverlay ? undefined : overlay });
      const picks = cap !== undefined ? togglePick(aim.picks ?? [], targetId) : [targetId];
      if (cap !== undefined && picks.length < cap) return setAim({ ...aim, picks });
      if (fireSkirmish(aim, picks)) return;
    } else if (aim.mode === "activate") {
      encounter.sendIntent(buildActivateIntent(attacker.id, aim.actionId.trim(), { target: targetId }));
    } else if (aim.mode === "tech_attack") {
      // System-granted tech attack — the clicked
      // token is the target; `aim.actionId` is the composite `<itemId>:<actionIndex>` id
      // `techAttackActions` freezes. Mirrors `quickTechIntent`'s own `tech_attack` route
      // (`packages/rules/src/encounter/quickTech.ts:152-161`) directly on the wire: `request_attack`
      // with `tech: true` and `weapon: aim.actionId` — never `request_activate` (that intent has no
      // `tech`/roll concept, and the room's legality wall (`legalTechAttackWeaponIds`) only admits
      // this id via the `tech: true` path). `outOfRangeTargetIds` above already dims an illegal
      // target (Sensors range, tech-immunity, same-side) before this ever fires.
      encounter.sendIntent(directAttackIntent(state, aim, targetId));
    } else if (aim.mode === "ram") {
      // Ram (melee-control-actions) — the clicked token is the target; the knock-back push was
      // pre-declared on the aim in the chooser. The synthetic `act_ram` weapon resolves it server-side
      // (vs Evasion, Prone, optional 1-space forced_move); the engine stays the wall on adjacency + Size.
      encounter.sendIntent(directAttackIntent(state, aim, targetId));
    } else if (aim.mode === "grapple") {
      // Grapple — the clicked token is the target; no up-front declaration
      // (no push, no choices). The synthetic `act_grapple` weapon resolves it server-side (vs Evasion,
      // mutual bind on a hit — real Engaged + the invented `Grappled` overlay, partner-stamped both
      // ways); the engine stays the wall on adjacency (no Size gate, unlike Ram).
      encounter.sendIntent(directAttackIntent(state, aim, targetId));
    } else if (aim.mode === "improvised_attack") {
      // Improvised Attack (RAW Core p.71) — the clicked token is the
      // target; no up-front declaration (no weapon needed, no choices). The synthetic
      // `act_improvised_attack` weapon resolves it server-side (vs Evasion, a FULL action, REAL 1d6
      // kinetic damage on a hit — unlike Ram/Grapple's no-damage shape); the engine stays the wall on
      // adjacency (no Size gate).
      encounter.sendIntent(directAttackIntent(state, aim, targetId));
    } else if (aim.mode === "release") {
      // Release — the clicked token is this
      // release's target; no up-front declaration (the readied verb + weapon are already pinned in
      // the armed hold). `buildReleaseIntent` derives the weapon/tech pair off the held record and
      // stamps `releasedBy` so the room resolves it as an out-of-turn release, not a standard attack
      // — the engine is the wall on fire-time-variable legality (range/target/loaded); an illegal
      // release is a rejected no-op that leaves the hold armed for a retry. If the hold
      // lapsed/dropped between arming RELEASE NOW and this click (a rare race), there's nothing to
      // release — just clear the aim.
      const held = heldAction(state.activation, attacker.id);
      if (!held) return setAim(null);
      encounter.sendIntent(buildReleaseIntent(attacker.id, { kind: "unit", id: targetId }, held));
    } else if (aim.mode === "barrage") {
      // Barrage (`barrage-sequential-resolution`) — roll-as-you-assign. The clicked
      // token is THIS shot's target; the armed weapon card is the weapon. One `request_barrage` resolves
      // immediately (opener charges the Full + opens the lock, continuation auto-closes when no legal
      // attack remains — all server-side off the synced lock). **Auto-disarm the WEAPON** (clear
      // weaponId), keeping Barrage armed so the chooser re-renders the remaining legal cards from the
      // lock; the chooser closes itself once the Barrage completes.
      // A weapon that attacks several targets at a time collects its picks first (`multiTarget.ts`).
      const cap = multiTargetCap({ state, aim, overlay: loadingOverlay ? undefined : overlay });
      const picks = cap !== undefined ? togglePick(aim.picks ?? [], targetId) : [targetId];
      if (cap !== undefined && picks.length < cap) return setAim({ ...aim, picks });
      fireBarrage(aim, picks);
      return;
    } else if (aim.mode === "search") {
      // Search from the board: the clicked Hidden token is the target. `outOfRangeTargetIds` drops
      // every token the Search card would not offer, so only a legal pick reaches here.
      encounter.sendIntent(buildSearchIntent(attacker.id, targetId));
    } else if (aim.mode === "mount" || aim.mode === "jockey" || aim.mode === "ride") {
      // A pilot's Mount, Jockey or Ride from the board (`pilot-command-menu` 2.4): the clicked
      // mech is the pick, the board twin of the chooser card's select, as Search's is.
      // `outOfRangeTargetIds` drops every mech the card would not offer.
      const build = aim.mode === "mount" ? buildMountIntent : aim.mode === "jockey" ? buildJockeyIntent : buildRideIntent;
      encounter.sendIntent(build(attacker.id, targetId));
    } else if (aim.mode === "quick_tech") {
      // Quick Tech — the option is picked in-panel (`aim.optionId`,
      // armed on the QUICK TECH row itself, mirroring Skirmish's weapon-card pick); the clicked token
      // is the target. `quickTechPickIntent` routes the chosen option through `quickTechIntent`
      // (`@lancer-vtt/rules`) to its resolver unchanged — activate for Scan/Lock On/Bolster, a tech
      // `request_attack` for Invade, with an "Invade ›" pick's option pre-answered. The server stays
      // the wall (Sensors range, tech-immunity, self, willing-ally-Invade deferral, the option's own
      // legality); `outOfRangeTargetIds` mirrors it above so an ineligible target dims and falls
      // through to plain select instead of sending a doomed shot.
      encounter.sendIntent(quickTechPickIntent(aim, targetId));
    } else if (aim.mode === "full_tech") {
      // Full Tech — the option for THIS pick is
      // armed in-panel (`aim.optionId`, mirroring Quick Tech's own row-arm); the clicked token is the
      // pick's target. `fullTechPickIntent` sends ONE `request_full_tech` pick — the server
      // disambiguates opener (no open `full_tech` lock for this actor: charges the Full action, opens
      // the mid-attack lock) from continuation (a lock is already open: free, auto-closes at 2 fired)
      // off the synced `AttackLock`, exactly like Barrage. `outOfRangeTargetIds` mirrors the SAME
      // `techTargetReason` wall Quick Tech uses so a doomed pick never even sends.
      encounter.sendIntent(fullTechPickIntent(aim, targetId));
      // Re-arm (don't clear the aim) — mirrors Barrage's per-shot re-arm: the chooser stays open with
      // `optionId` reset to `""` so pick 2 is armed from the SAME chooser, not a fresh arm click. The
      // server auto-closes the lock at 2 picks; the chooser reads that off the live `AttackLock`
      // (`FullTechChooser` in `CombatWindows.tsx`), not this aim.
      setAim({ ...aim, optionId: "", accuracy: 0 });
      return;
    } else {
      // Stabilize never fires from a canvas click (`aimResolvesToFire` excludes it) — its target is
      // picked in the in-panel chooser. A stray click falls through to select.
      return;
    }
    setAim(null);
  };
  // Activate with no spatial target (an undeclared `choose` raises the prompt modal on this client).
  // The attacker is the armed aim's own combatant (the inspector whose action bar is armed).
  const onSelfActivate = (actionId: string) => {
    if (!aim || !actionId) return;
    encounter.sendIntent(buildActivateIntent(aim.attackerId, actionId));
    setAim(null);
  };
  // Token/row selection, with click-to-target woven in: while an action is armed, a click on a
  // combatant *other than the armed attacker* fires the action at it (instead of re-selecting);
  // otherwise it selects (single-click = select; double-click = inspect, a separate gesture).
  // The one selection path: a token press that does not fire, or a round header "to act" row. The command
  // menu derives from the selection (`shownMenu`), so it shows whatever this selects.
  const selectUnit = (id: string | null) => setSelectedId(id);
  const handleSelect = (id: string | null) => {
    // A unit pick takes the click: a ringed unit answers, anything else does nothing.
    if (unitPick) {
      if (id === null || !unitPick.ids.includes(id)) return;
      if (unitPick.multi) setPickedUnits({ promptId: unitPick.promptId, ids: togglePicked(unitPick, livePicked, id) });
      else encounter.answerPromptById(unitPick.promptId, id);
      return;
    }
    if (aimResolvesToFire(aim, id) && id && state.combatants[id]) {
      // Hard-disable: the reach wash is a wall, not a hint. An out-of-range target can't be
      // fired at (mirrors the server's `checkAttackRange` reject), so the click never sends the
      // doomed shot. It keeps the selection on the attacker too: the command menu shows the
      // selection, and switching it mid-aim would take the menu away with the aim still armed. A
      // double-click still inspects the unit.
      if (targetGate?.has(id)) return;
      // Touch has no hover, so the first tap on a target expands its forecast and a second commits
      // (`targetTap.ts`). A mouse click commits at once: the card was already open under the pointer.
      if (aim && targetTapStep({ pointerType: lastPointerTypeRef.current, targetId: id, expandedId: touchExpandedId, hasForecast: targetBadges?.has(id) ?? false }) === "expand") {
        setTouchExpanded({ aim, targetId: id });
        return;
      }
      fireAt(id);
      return;
    }
    setTouchExpanded(null);
    // An empty-board click mid-aim is the aim's own commit (an area attack's cell, handled on the
    // stage) or nothing: it never deselects, so the menu and the fire card keep their exits while
    // the aim stays armed. Esc is how to put an aim down.
    if (id === null && aim !== null) return;
    selectUnit(id);
  };
  // `InspectorBody`'s props for a unit, for the expanded unit panel (PLAY's only sheet). The one call
  // site that projects a raw `state.combatants` entry for the sheet: `role` falls back to `undefined`
  // (never masks) only before the session's role is known, the ordinary pre-welcome state, not a leak.
  const inspectorBodyProps = (c: Combatant | null) => ({
    combatant: c ? projectForDisplay({ combatant: c, viewerSlot: encounter.actorId, role: session.role ?? undefined, state }) : null,
    slots: session.slots,
    state,
    actorId,
    encounter,
    roster,
    aim,
    setAim,
    onSelfActivate,
    fireAt,
    onDeclineOverwatch: declineOverwatch,
    onError: setInspectorError,
    error: inspectorError,
    canControl: c ? canControl(c) : false,
    canEditBase: isGm,
    canConceal: isGm,
    isGm,
    bot,
    onOpenDice: openDice,
  });
  // EDIT: double-click a token → open/focus its per-unit definition window. The registry keys by
  // `inspector:<combatantId>`, so re-opening dedupes & raises (no duplicate); the stagger index
  // offsets only a first open so a second/third inspector doesn't land exactly atop.
  const openInspector = (id: string) => {
    if (!state.combatants[id]) return;
    const openInspectors = windows.registry.order.filter((wid) => inspectorCombatantId(wid) !== null).length;
    windows.open(inspectorSpec(id, openInspectors, { kind: state.combatants[id]!.kind }));
  };
  // PLAY has no unit inspector window (`projects/unit-panel.md` Phase 3): a unit's sheet is the unit
  // panel, expanded. Select the unit and grow its panel. While an aim is armed the selection stays
  // on the attacker (the menu and the fire card hold its exits), so another unit's double-click
  // there is only its clicks.
  const openUnitSheet = (id: string) => {
    if (!state.combatants[id]) return;
    if (aim !== null && id !== selectedId) return;
    selectUnit(id);
    setSheetOpen(true);
  };
  const inspectUnit = (id: string) => (viewMode === "play" ? openUnitSheet(id) : openInspector(id));
  // The armed shot the Map key's resolved register is relative to (`terrain-legibility` D4/D6).
  // `null` whenever no attack is armed or its attacker has gone — with no attacker there is nothing
  // honest to report, and the register is omitted entirely rather than guessed at. The weapon is
  // resolved through `weaponProfileForAim`, the SAME seam the reach wash and `armedCellSightlines`
  // read, so the pane's sentences and the board's hatch can never be describing different weapons.
  const tileArmed = (() => {
    if (!aim) return null;
    const attacker = state.combatants[aim.attackerId];
    if (!attacker) return null;
    return { attacker, weapon: weaponProfileForAim(attacker, aim), overlay };
  })();

  // Double-click a rendered room's floor → open/focus its per-room inspector. Same
  // dedupe/raise lifecycle as the unit inspector, keyed `room-inspector:<roomId>`; the stagger index
  // offsets only a first open so a second room inspector doesn't land exactly atop the first.
  const openRoomInspector = (roomId: string) => {
    if (!(state.rooms ?? {})[roomId]) return;
    const open = windows.registry.order.filter((wid) => roomInspectorRoomId(wid) !== null).length;
    windows.open(roomInspectorSpec(roomId, open));
  };

  // "Take Overwatch" on a move-checkpoint prompt: don't
  // answer the prompt yet (the interleaving contract — the "take" is a SIBLING resolution, not an
  // answer payload). Instead arm the SAME Overwatch aim the NPC inspector's own row already arms
  // (`reaction: true` on a plain skirmish draft) for the WATCHER,
  // and open the watcher's own inspector so its `SkirmishChooser` panel is immediately usable — the
  // decider picks a weapon exactly as any manual Overwatch.
  //
  // **The target is AUTO-FILLED to the mover**:
  // the checkpoint's mover is fixed by the trigger — the very unit whose declared move tripped this
  // offer — so requiring the watcher to re-target it on the board is a redundant step. `fixedTargetId`
  // pins it onto the aim, and the `SkirmishChooser` renders an explicit FIRE button that resolves
  // straight against the mover (the weapon/profile/accuracy pick stays live). The mover is still
  // parked exactly at its declared `from` (the move is unapplied) and the board affordance below
  // (`checkpointPromptMoverId`) highlights it, so a board click on the mover ALSO still fires (the
  // ordinary click-to-fire canvas pipeline is unchanged) — the FIRE button just makes the target
  // pick unnecessary. Either path runs `fireAt`'s own `overwatchTake` bookkeeping.
  //
  // **The weapon is preselected when exactly one reaches** (`overwatchTakeWeapon`: armable and
  // within THREAT of the mover); with several the chooser lists the reaching ones first and greys
  // the rest (`overwatch-mid-move` 3.2).
  const startOverwatchTake = () => {
    const p = encounter.prompt;
    const moverId = p ? checkpointMoverId(p.intent) : undefined;
    if (!p || moverId === undefined) return;
    const watcherId = moveCheckpointWatcherId(p.promptId);
    if (watcherId === undefined) return;
    // Capture `promptId` + `intent` into the LOCAL `overwatchTake` state right now, while the
    // checkpoint is still `encounter.prompt` (the head) — mirrors `promptPolicy.ts:285`'s
    // `const prompt = obs.prompt` local capture. The later re-submit answers by this captured id
    // (`answerPromptById`), never by re-reading `encounter.prompt`, which may have moved on to a
    // nested sibling prompt by then.
    setOverwatchTake({ promptId: p.promptId, intent: p.intent, watcherId, moverId, fired: false });
    const watcher = state.combatants[watcherId];
    const weaponId = watcher ? overwatchTakeWeapon({ combatant: watcher, state, targetId: moverId }) : "";
    setAim({ attackerId: watcherId, mode: "skirmish", weaponId, profileId: "", accuracy: 0, reaction: true, fixedTargetId: moverId });
    // The take's chooser (FIRE, Decline) lives on the watcher's sheet: select it and expand.
    selectUnit(watcherId);
    setSheetOpen(true);
  };

  // Resolve a combatant id to the world point `CombatStage` should pan its camera to
  // (`combat-chip-camera-focus`) — not passed as a bare id so `CombatStage` doesn't need to know how a
  // footprint's center is derived, only ever centering the view on a point. `focusSeqRef`/`focusPoint`
  // are declared up with the other hooks, above the loading early-return.
  const focusOnCombatant = (id: string) => {
    const c = state.combatants[id];
    if (!c?.pos) return; // unplaced (no board position) — nothing to pan to
    const box = footprintBox(asSquareCell(c.pos), c.base.size);
    focusSeqRef.current += 1;
    setFocusPoint({ x: (box.minX + box.n / 2) * CELL, y: (box.minY + box.n / 2) * CELL, seq: focusSeqRef.current });
  };
  // A round header "to act" row: the unit may be off-screen or off the board, so select it and pan
  // the camera to it (a no-op when unplaced; `combat-chip-camera-focus`). Its unit panel shows it,
  // and END TURN is on its menu (`turn-presence` Decision 2 A).
  const findUnit = (id: string) => {
    selectUnit(id);
    focusOnCombatant(id);
  };

  const nameOf = (id: string) => state.combatants[id]?.name ?? id;

  // Dispatch a command through the encounter room. The old client-local LOG/RESOLUTION windows were
  // retired — command activity + resolutions now bridge SERVER-SIDE
  // into the shared COMMS.app feed (for everyone, not just this client), so dispatch is a plain
  // pass-through with no local logging wrapper. The one exception THIS wrapper makes: a `move_token`
  // (`moveCommandType`'s PLAY branch — EDIT's `place_token` is unaffected) rides the `request_move`
  // **intent** instead of the raw command (the two-command-tiers checkpoint seam); `CombatStage`'s
  // drag/click-place call sites stay untouched, this single seam intercepts every `move_token` they
  // build. `end_turn` needs NO exception here: `RemoteEncounterController.dispatch`'s own override
  // (`remoteEncounterController.ts`) reroutes every `end_turn` onto `request_end_turn` one layer
  // down, so `encounter.dispatch(command)` below already does the right thing for
  // the inspector headers' and the command menu's end-turn buttons too — see that
  // override's doc for why the seam sits there instead of here (`decisions/0164` D2).
  // `opts.choices` are a dragged Boost's opt-in answers (`CombatStage`'s Boost card), which ride the
  // `request_move` the room folds the Boost from.
  const dispatch = (command: EncounterCommand, opts: { choices?: Record<string, string> } = {}) =>
    command.type === "move_token"
      ? encounter.dispatchMove(command.combatantId, command.to, {
          actorId: command.actorId,
          ...(command.path !== undefined ? { path: command.path } : {}),
          ...(opts.choices !== undefined ? { choices: opts.choices } : {}),
        })
      : encounter.dispatch(command);

  // Commit a locally-accepted move to PRESENTATION (`CombatStage`'s `onLocalMove` prop) — GATED to
  // EDIT's `place_token` ONLY. PLAY's `move_token` no longer has
  // an optimistic local paint to dedupe: the drag no longer moves the real node at all (a drag-ghost
  // does instead — `CombatStage`'s `finishTokenDrag`), so the dragger's real token walks off the
  // authoritative echo like every other client, same as the diff-path `ingest` already animates for a
  // bystander (`token-slide-parity.e2e.ts`'s proven walk). This supersedes the former `isMoveHeld`
  // read-before-commit gate — that gate existed only to protect
  // PLAY's optimistic paint from a predicted checkpoint hold, and PLAY has no optimistic paint left to
  // protect. `CombatStage` only calls this prop from the EDIT branch now, but the mode check here is
  // the self-defending source of truth, not a call-site-discipline assumption.
  const onLocalMove = (id: string, pos: SquareCell) => {
    if (moveCommandType !== "place_token") return;
    commitLocalMove(id, pos);
  };

  // Open the transient DEPLOY picker for a source (the UNITS panel's ADD buttons).
  const openPicker = (kind: PickerKind) => {
    setPickerKind(kind);
    windows.open(DEPLOY_PICKER_WINDOW);
  };

  // The encounter's single cross-mode undo/redo availability + next-command labels,
  // for the tool rail's TABLE buttons (and the labels they show on hover).
  const undoState = encounter.undoState;


  // The board affordance highlighting the mover whose move is paused: while THIS client holds a
  // live move-checkpoint prompt (we're the watcher being
  // asked, or a GM backstop recipient), name the mover — the token that hasn't moved, and won't,
  // until this decision resolves. Reuses the board's ONE existing highlight primitive (the
  // `selectedId`-driven halo ring, `pixiCombatBoard.ts`'s `setSelection`) rather than inventing a
  // second one; it deliberately SUPERSEDES the player's own last click while the prompt is live
  // (mirrors how a direction prompt already supersedes the armed-attack aim above) — the paused
  // mover is the one thing worth looking at right now. Only overrides the CANVAS token halo (the
  // `CombatStage` prop below); list-selection surfaces (TURN PANEL / UNITS) keep reading the
  // player's own `selectedId` untouched.
  const checkpointPromptMoverId =
    encounter.prompt && isMoveCheckpointPromptId(encounter.prompt.promptId) ? (checkpointMoverId(encounter.prompt.intent) ?? null) : null;
  const boardSelectedId = checkpointPromptMoverId ?? selectedId;

  // Undo/redo go straight to the server and arrive back as a state reconcile. Gated on availability
  // so a keyboard undo with nothing on the stack is a no-op. Both the keyboard refs and the rail's
  // buttons go through these.
  const doUndo = () => {
    if (!undoState.canUndo) return;
    bumpUndoRedoSeq(); // void any in-flight PLAY-move ghost this rewind rewrites (see `undoRedoSeq`)
    encounter.undo();
  };
  const doRedo = () => {
    if (!undoState.canRedo) return;
    bumpUndoRedoSeq();
    encounter.redo();
  };
  undoRef.current = doUndo;
  redoRef.current = doRedo;

  const surface = (
    <div className="combat-app unit-sheet-host cw-host" ref={attachApp}>
      {/* COMBAT has no top bar (`decisions/0203` point 9, `website-shell` 4.3): undo/redo, the VIEW
          toggles, EDIT|PLAY and END COMBAT live in the board tool rail; LIVE and the clock in the shell
          strip's footer; UNITS and Map setup are the rail's SCENE panels; DICE is a rail page. The
          encounter's name is not shown. */}
      {/* The GM's stale-content notice (`terraformer-tiles-fix`). It sits in flow above the board;
          the round header is absolutely positioned inside `.combat-body` below it. */}
      {isGm && <StaleContentNotice />}

      {/* The board fills the surface below the chrome. Every authoring tool lives in a window
          (Units/Terrain) and pan/select folds into the canvas (select is default; space-held /
          middle-drag pans). The map has one fixed-width flex sibling, COMMS on the right (the PLAY
          TURN rail is retired, `decisions/0190`); the map's `.combat-stage-wrap` is `flex:1` +
          ResizeObserver-sized, so it takes whatever width is left. */}
      <div className="combat-body">
        {/* THE ROUND HEADER — the trapezoid hanging from the board's top edge (`decisions/0190` point 3,
            `decisions/0203` point 9): the round, whose pick it is, each side's counts and the GM's NEXT
            ROUND in PLAY, and the SITREP readout in either mode. Absolutely positioned and centred on
            `.combat-body`, so it costs the board no height. */}
        <RoundHeader
          // While a held roll is ahead the header reads the board's presentation, so it doesn't drop
          // a unit from "to act" before the table has rolled its check (`chained-resolution-sequence`
          // 4.4). Its PICK commands still go to the server, which checks them against live state.
          state={holdAhead && presentation !== null ? presentation : state}
          viewMode={viewMode}
          isGm={isGm}
          actorId={actorId}
          dispatch={encounter.dispatch}
          clearance={sitrepClearanceTotal}
          onFindUnit={findUnit}
          onPanTo={focusOnCombatant}
        />
        {viewMode === "play" && <TurnBanner state={state} />}
        {override && <OverrideBanner />}
        <CombatStage
          cols={extent.width}
          rows={extent.height}
          combatants={combatants}
          tokenImageCtx={tokenImageCtx}
          tokenChrome={tokenChrome}
          terrain={state.terrain}
          edges={state.edges}
          ground={encounterGround(state)}
          rooms={state.rooms ?? {}}
          sitrepZones={sitrepZones}
          areaEffects={state.areaEffects ?? {}}
          annotations={encounterAnnotations(state)}
          drawTool={toolsDisabled ? null : drawTool}
          esc={esc}
          drawColor={drawColor}
          fullGrid={viewMode === "play" && playGrid.on}
          terrainMechanics={terrainMechanics.on}
          brush={liveBrush}
          tool={liveTool}
          coverGrade={coverGrade}
          hazard={hazard}
          tile={tile}
          wallHeight={wallHeight}
          wallCoverGrade={wallCoverGrade}
          eraseMode={toolsDisabled ? false : eraseMode}
          autoWall={autoWall}
          objectPlan={objectPlan}
          selectedId={boardSelectedId}
          reachable={splitPick || cellPick ? null : reachable}
          sensorRange={sensorReach}
          boostRange={splitPick || cellPick ? null : boostRange}
          threatRings={threatRingsView}
          threatRingsAt={threatRingsAt}
          pathPreview={pathPreview}
          trampleTrail={trampleTrail}
          isPlanningMove={(id) => moveStateFor(id)?.planning === true}
          moveForecast={moveForecast}
          boostDropRiders={(id, dest, path) => override ? null : boostDropRiders({ state, combatantId: id, to: dest, ...(path !== undefined ? { path } : {}), actorId: encounter.actorId, isGm })}
          activeUnitName={
            state.activation.turn.phase === "acting" ? (state.combatants[state.activation.turn.actor]?.name ?? null) : null
          }
          registerNode={registerNode}
          unregisterNode={unregisterNode}
          getNode={getNode}
          setTicker={setTicker}
          onLocalMove={onLocalMove}
          watchSettle={watchSettle}
          noteMoveHeld={noteMoveHeld}
          moveRejectionSeq={encounter.rejection?.seq ?? 0}
          undoRedoSeq={undoRedoSeq}
          floats={floats}
          onFloatDone={onFloatDone}
          projectiles={projectiles}
          onProjectileDone={onProjectileDone}
          impacts={impacts}
          onImpactDone={onImpactDone}
          chainFocus={chainFocus}
          isAnimating={isAnimating}
          onSkipAnimations={skip}
          attackRange={cellPickWash ?? splitKeys ?? attackRange}
          // Only an ARMED aim dims the units it cannot reach; a hover preview rings the legal ones and dims nothing.
          outOfRangeTargetIds={unitPickDim ?? (aim ? targetGate : null)}
          attackAffected={attackAffected}
          attackLosExcluded={attackLosExcluded}
          attackArea={splitPick || cellPick ? true : attackArea}
          cellPickArmed={splitPick || cellPick ? true : cellPickArmed}
          cellSightlines={cellPickSightlines ?? splitSightlines ?? cellSightlines}
          pendingDeploy={cellPickDeploy ?? pendingDeploy}
          targetPicking={targetPicking || unitPick !== null}
          onCommitCell={onCommitCell}
          directionPicking={directionPromptActive}
          onCommitDirection={onCommitDirection}
          onSelect={handleSelect}
          onInspect={inspectUnit}
          onInspectRoom={openRoomInspector}
          focusPoint={focusPoint}
          dispatch={dispatch}
          actorId={actorId}
          isGm={isGm}
          canControl={canControl}
          // 2.4 / D4: the wall function is UNTOUCHED — what moved is the state it is asked
          // about. A `"ready"` unit this viewer controls by strict slot identity is draggable
          // pre-activation, because the room folds an `activate_unit` in front of its
          // `request_move` and then accepts the move; everything else still reads exactly as
          // before. `previewIfControlled` is the same expression the movement wash, the drag-path
          // trail and `dispatchMove`'s dry-run compose against — one mirror, four readers.
          // The GM override drags any placed unit: its drop is a forced move.
          canPlayMove={(c) => (override ? c.pos != null : mayPlayMove(previewIfControlled(state, c, { actorId: encounter.actorId, isGm }), c))}
          placing={placing}
          moveCommandType={moveCommandType}
          onPing={encounter.sendPing}
          ping={encounter.latestPing}
          onCursor={encounter.sendCursor}
          onHoverCell={setHoverInfoCell}
          cursors={cursorsWithLabels}
          slotNameOf={slotNameOf}
          onMovePreview={encounter.sendMovePreview}
          movePreviews={encounter.movePreviews}
          onTokenHover={setHoveredTokenId}
          targetRings={targetRings}
          targetBadges={targetBadges}
          expandedTargetId={expandedTargetId}
          measureArmed={measure.armed}
          onMeasurePointer={measure.onPointer}
          measureRender={measure.render}
          measureDebugRef={measure.debugRef}
          inspectArmed={inspect.armed}
          inspect={inspect.cell && inspectData ? { cell: inspect.cell, body: <InspectReadout data={inspectData} /> } : null}
          overlay={
            <>
              {/* A held roll's stakes banner, on every board (`chained-resolution-sequence` 3.2). */}
              {hold && (
                <SequenceHoldBanner
                  key={hold.key}
                  hold={hold}
                  isGm={isGm}
                  canRelease={isGm || controlsCombatant(state.combatants[hold.roller], actorId)}
                  rollerName={state.combatants[hold.roller]?.name ?? hold.roller}
                  onRelease={() => encounter.sendHoldRelease(hold.key, hold.roller)}
                />
              )}
              {/* The board tool rail (`decisions/0191`): the stage's right edge, every viewer, both modes. */}
              <BoardToolRail mode={viewMode} ctx={railCtx} disabled={toolsDisabled} />
              {mapKey.on && (
                <MapKeyPanel esc={esc} onClose={() => mapKey.set(false)} tile={{ state, cell: hoverInfoCell, armed: tileArmed }} legend={{ state }} />
              )}
              {viewMode === "play" && (
                <UnitColumn
                  slotRef={setTargetingSlot}
                  panel={unitPanel}
                  sheet={sheetOpen && selected ? <InspectorBody {...inspectorBodyProps(selected)} /> : null}
                  onExpand={() => setSheetOpen(true)}
                  onCollapse={() => setSheetOpen(false)}
                  menu={
                    <CommandMenuPresence unitId={menuUnitId}>
                      {menuUnit && menuState && (
                        <TokenCommandMenuHost
                          combatant={menuUnit}
                          menuState={menuState}
                          state={state}
                          actorId={actorId}
                          encounter={encounter}
                          aim={aim}
                          setAim={setAim}
                          onPreviewAim={setMenuPreviewAim}
                          onPreviewMove={setPreviewMove}
                          targetingSlot={targetingSlot}
                          onActiveScreen={setMenuScreen}
                          onStrip={sheetOpen ? () => setSheetOpen(false) : undefined}
                        />
                      )}
                    </CommandMenuPresence>
                  }
                />
              )}
              {/* THE ACTION ASKS (`ActionAsksDock`): answering re-submits the intent with the choice; the
                  modal's Cancel dismisses, which for a `move_checkpoint:` prompt RE-SUBMITS a decline
                  (`RemoteEncounterController.dismissPrompt`) so the parked move is never orphaned. It
                  shows the HEAD prompt (`RemoteEncounterController.prompt`). */}
              {promptDockShown && encounter.prompt && (
                <ActionAsksDock>
                  <PromptModal
                    prompt={encounter.prompt}
                    // "For <mech name>" — the one thing in the window that says WHICH combatant is being
                    // asked. An area attack raises a prompt per caught mech, sequentially, on the same
                    // client (`area-attack-reaction-offers` D1), and the authored sentence is identical
                    // every time; `undefined` (a decider this client's projected state cannot see) simply
                    // renders no line.
                    deciderName={promptDeciderName(state, encounter.prompt)}
                    // The attacker a damage reaction's question names ("Brace against Ronin's hit?").
                    sourceName={promptSourceName(state, encounter.prompt)}
                    onAnswer={(optionId) => {
                      // A move-checkpoint "take" is a SIBLING
                      // resolution, not an immediate answer — route into the targeting flow instead of
                      // calling `answerPrompt` here. Decline (and every non-checkpoint prompt) is unchanged.
                      if (isMoveCheckpointPromptId(encounter.prompt!.promptId) && optionId === OVERWATCH_TAKE_OPTION) {
                        startOverwatchTake();
                        return;
                      }
                      // "Pick where along the path" (a Covering Fire offer) opens the board pick; the
                      // clicked cell is the answer.
                      if (optionId === PICK_ALONG_PATH_OPTION) {
                        const pick = splitPickFor(encounter.prompt!);
                        if (pick) {
                          setAim(null);
                          setSplitPick(pick);
                        }
                        return;
                      }
                      encounter.answerPrompt(optionId);
                    }}
                    onDismiss={() => encounter.dismissPrompt()}
                  />
                </ActionAsksDock>
              )}
              {targeting && (
                <TargetingMode
                  banner={pickCap !== undefined && isPickingAim(aim) ? multiTargetBanner(targeting, { cap: pickCap, names: (aim.picks ?? []).map((id) => state.combatants[id]?.name ?? id) }) : targeting}
                  onCancel={cancelAim}
                  hoverTarget={hoverTarget}
                  cancel={menuScreen !== "fire"}
                  {...(isPickingAim(aim) && aim.picks?.length ? { onConfirm: confirmPicks } : {})}
                />
              )}
              {splitPick && <TargetingMode banner={splitPickBanner(state, splitPick)} onCancel={() => setSplitPick(null)} hoverTarget={null} />}
              {unitPick && encounter.prompt && (
                <TargetingMode
                  banner={unitPickBanner({ pick: unitPick, title: encounter.prompt.prompt, names: livePicked.map((id) => state.combatants[id]?.name ?? id) })}
                  onCancel={() => encounter.dismissPrompt()}
                  cancel={boardPickDismissible}
                  hoverTarget={null}
                  {...(unitPick.multi ? { onConfirm: () => encounter.answerPromptById(unitPick.promptId, unitPickAnswer(livePicked)) } : {})}
                />
              )}
              {cellPick && encounter.prompt && (
                <TargetingMode
                  banner={cellPickBanner({ pick: cellPick, title: encounter.prompt.prompt, staged: liveStaged !== null })}
                  onCancel={() => (liveStaged ? setStagedCell(null) : encounter.dismissPrompt())}
                  cancel={liveStaged !== null || boardPickDismissible}
                  hoverTarget={null}
                  {...(liveStaged ? { onConfirm: confirmStagedCell } : {})}
                />
              )}
              {targeting && (
                <TargetForecastPanel
                  state={state}
                  overlay={loadingOverlay ? undefined : overlay}
                  intent={forecastIntent}
                  viewer={{ role: session.role ?? undefined, viewerSlot: encounter.actorId }}
                />
              )}
            </>
          }
        />
        {/* COMMS — the shared chat & activity feed, in the shell's fixed right column (`CombatComms`).
            This mount, not the shell's own, is the one shown while COMBAT holds the stage:
            `answerDecision` is threaded down so its `DecisionCard`s resolve a reaction over the SAME
            live encounter controller THE ACTION ASKS uses, the only mount with a live `/ws/encounter`
            socket to re-submit onto. */}
        <CombatComms>
          <CommsApp answerDecision={encounter.answerDecision} onHoverDecisionOption={onHoverDecisionOption} />
        </CombatComms>
      </div>

      {/* The DEPLOY picker — a floating window UNITS' ADD buttons open, in either mode (an unplaced
          unit's first placement is legal in both). GM-only, like UNITS. */}
      {isGm && (
        <CombatWindow
          windows={windows}
          esc={esc}
          spec={DEPLOY_PICKER_WINDOW}
          glyph={faPlus}
          title={pickerKind === "npc" ? "ADD ENEMY" : pickerKind === "pilot" ? "ADD PILOT" : "ADD MECH"}
        >
          <DeployPickerBody
            kind={pickerKind}
            compendium={compendium}
            // `unmechanized-content-diagnostic` D6 — pass the overlay ONLY once it has
            // settled. While loading it is the bare `SEED_OVERLAY`, against which nearly the
            // whole corpus reads unmechanized; `undefined` makes the picker say nothing
            // instead of tagging every row.
            overlay={loadingOverlay ? undefined : overlay}
            // …and the flag itself, so the picker can tell "still loading" from "the host has
            // none". The mech deploy hands its overlay to `spawnMech`, whose overlay-minted
            // affordance-record freeze is PERMANENT for that combatant — deploying in the loading window leaves a
            // mech with no limited-use charges for the rest of the fight, silently and forever.
            // `mechDeployHoldReason` holds the Mechs section until this clears.
            loadingOverlay={loadingOverlay}
            // A failed load keeps the hold closed and names why (`landing-zone` 8.3).
            overlayError={overlayError}
            pilots={pilots}
            rosterLoading={roster.loading}
            actorId={actorId}
            disabled={toolsDisabled}
            canConceal={isGm}
            ownerSlots={isGm ? session.slots : undefined}
            npcWear={isGm ? roster.state?.npcWear ?? {} : undefined}
            onClearNpcWear={
              isGm
                ? (key) => {
                    const r = roster.dispatch({ type: "clear_npc_wear", actorId, key });
                    return r.ok ? null : r.error;
                  }
                : undefined
            }
            dispatch={dispatch}
            onDeployed={setSelectedId}
            onClose={() => windows.close(DEPLOY_PICKER_WINDOW.id)}
          />
        </CombatWindow>
      )}

      {/* Per-unit inspectors — dynamic `inspector:<combatantId>` windows, EDIT only, opened by
          double-clicking a token: they edit the unit *definition* (rename / base stats / delete).
          PLAY has none; a unit's sheet there is the unit panel, expanded (`UnitColumn`). Rendered
          straight off the active registry's open ids (already mode-keyed). */}
      {viewMode === "edit" &&
        windows.registry.order
          .map((wid) => inspectorCombatantId(wid))
          .filter((cid): cid is string => cid !== null)
          .map((cid) => {
            const c = state.combatants[cid] ?? null;
            return (
              <CombatWindow key={cid} windows={windows} esc={esc} spec={inspectorSpec(cid, 0, { kind: c?.kind })} glyph={faAddressCard} title={`EDIT · ${c?.name ?? cid}`}>
                <EditInspectorBody combatant={c} state={state} actorId={actorId} slots={session.slots} disabled={toolsDisabled} dispatch={dispatch} />
              </CombatWindow>
            );
          })}

      {/* Per-room inspectors — dynamic `room-inspector:<roomId>`
          windows opened by double-clicking a rendered room's floor. Rename / delete-room / per-rect ✕
          all route through the EXISTING `set_room` / `remove_room` commands (synced + undoable). Like
          the unit inspectors, rendered straight off the active registry's open ids. */}
      {windows.registry.order
        .map((wid) => roomInspectorRoomId(wid))
        .filter((rid): rid is string => rid !== null)
        .map((rid) => {
          const room = (state.rooms ?? {})[rid] ?? null;
          return (
            <CombatWindow
              key={`room:${rid}`}
              windows={windows}
              esc={esc}
              spec={roomInspectorSpec(rid)}
              glyph={faDoorOpen}
              title={`ROOM · ${room?.name ?? rid}`}
            >
              <RoomInspectorBody room={room} actorId={actorId} disabled={toolsDisabled} dispatch={dispatch} isGm={isGm} />
            </CombatWindow>
          );
        })}

      {/* The transient rejection toast: when the server refuses an
          optimistic command / resolution intent (the controller reverted the phantom apply and relayed
          the reason), surface a brief categorized banner so the player/GM learns *why* — the safety net
          for the rejections an affordance didn't pre-empt. Reads the live `encounter.rejection` signal;
          `isGm` flips a player's raw ownership-gate string to human copy. */}
      <RejectionToast rejection={encounter.rejection} isGm={isGm} />

      {/* The AUTO-ACTIVATION acknowledgement (`projects/pre-activation-affordances.md` 4.1): this
          project removed the ACTIVATE TURN gate, so a ready mech's first action now opens its turn
          on its own — which means clicking an ability on the WRONG mech silently commits that
          mech's turn. This transient, non-blocking, undismissable card names the unit whose turn
          just opened, so the misclick is something the player sees rather than discovers a round
          later. Local to the acting client by construction (`encounter.autoActivation` is only ever
          armed by this client's own gesture); a bystander's feedback is the turn tracker flipping.
          Never fires on an explicit ACTIVATE TURN press. */}
      <AutoActivationBanner
        notice={encounter.autoActivation}
        name={encounter.autoActivation ? nameOf(encounter.autoActivation.combatantId) : ""}
      />

      {/* The attacker/source-controller REACTION-WAIT banner: when our action halts mid-resolution for someone else's reaction — a mech's
          Brace, a third party's Valiant Aid, any cross-actor decision — we show a transient "waiting for
          {decider}" instead of a frozen board, derived truthfully off the live `game_decision` map so it
          covers an NPC target exactly like a mech target and clears itself the moment the server resolves
          the decision — never a client-side `target?.kind === "mech"` guess. */}
      <ReactionWaitBanner wait={resolutionWait} />

      {/* The mover MOVE-HOLD banner: when our own
          declared move is predicted to halt a checkpoint gather, the prompt routes to the WATCHER + GM
          (not us) — this transient banner is our only signal while it's unacked. See
          `remoteEncounterController.ts`. */}
      <MoveHoldBanner
        pending={encounter.pendingMove}
        name={encounter.pendingMove ? nameOf(encounter.pendingMove.combatantId) : ""}
        decider={moveHoldDecider}
      />

      {/* The TABLE-WIDE paused notice: the two banners above cover the mover and the decider(s);
          this covers everyone else — a bystander spectator who would otherwise watch the board simply
          stop mid-turn with no explanation. Server-broadcast (`encounter_move_paused_event`, every
          connected client), suppressed on THIS client when it's already covered by a more specific
          signal (it IS the mover — `MoveHoldBanner` above already names it — or it's actively holding
          the live move-checkpoint prompt / mid-take-flow for this exact pause). */}
      <TablePausedBanner
        pausedMoverId={encounter.tablePausedMoverId}
        suppress={
          encounter.pendingMove?.combatantId === encounter.tablePausedMoverId ||
          overwatchTake !== null ||
          (encounter.prompt !== null && isMoveCheckpointPromptId(encounter.prompt.promptId))
        }
        name={nameOf(encounter.tablePausedMoverId ?? "")}
        decider={tablePausedDecider}
      />

      {/* Dock-zone preview — the translucent target painted while a window is dragged to an edge. */}
      <DockZonePreview rect={windows.zonePreview} />

      {isGm && endPanelOpen && (
        <EndCombatPanel
          state={state}
          roster={roster.state}
          compendium={compendium}
          encounterId={encounterId}
          actorId={encounter.actorId ?? "gm"}
          dispatch={roster.dispatch}
          // Ending marks the fight over (`end_combat`) and closes the panel; the server then puts the
          // table on Narrative (`decisions/0203` point 2). Once the fight is over the panel only commits.
          {...(isCombatOver(state)
            ? {}
            : {
                onEnd: () => {
                  const res = encounter.dispatch({ type: "end_combat", actorId: encounter.actorId ?? "gm", at: new Date().toISOString() });
                  if (res.ok) setEndPanelOpen(false);
                  return res;
                },
              })}
          onClose={() => setEndPanelOpen(false)}
        />
      )}
    </div>
  );
  // A shared component that also renders outside COMBAT.app (the glossary `Term`) finds the registry here.
  return (
    <EscLayersContext.Provider value={esc}>
      <RangePreviewContext.Provider value={rangePreview}>{surface}</RangePreviewContext.Provider>
    </EscLayersContext.Provider>
  );
}

/**
 * COMBAT's COMMS, in the shell's fixed COMMS column (`decisions/0203` point 4): a portal into the
 * column's body, which shows nothing else while COMBAT holds the stage, so there is one feed, not
 * two. Outside the shell (no stage context) it falls back to COMBAT's own right sidebar.
 */
function CombatComms({ children }: { children: ReactNode }) {
  const stage = useStage();
  if (stage === undefined) return <div className="combat-comms-sidebar">{children}</div>;
  return stage.commsSlot === null ? null : createPortal(children, stage.commsSlot);
}
